{
  "$schema": "http://json-schema.org/draft-07/schema#",
  "$id": "https://hopdrive.com/generated/config.schema.json",
  "title": "HopDrive Configuration Schema",
  "description": "Configuration schema for HopDrive Organizations and Customers. Configs are merged hierarchically: Organization config provides defaults, Customer config overrides. Built from canonical definition modules.",
  "type": "object",
  "properties": {
    "version": {
      "type": "string",
      "description": "Configuration schema version for compatibility (e.g., \"1.0.0\").",
      "x-hopdrive-label": "Version",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "region",
        "organization",
        "customer",
        "workflowset",
        "raterulegroup",
        "move"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "metadata"
    },
    "api_enabled": {
      "type": "boolean",
      "description": "Activate/Deactivate the API feature for clients at this level. Currently, this is only a visual indicator of the client to show that they use the API. In the future, this switch may behave differently.",
      "x-hopdrive-label": "API Enabled",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "organization",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "integrations"
    },
    "api_config": {
      "type": "object",
      "properties": {
        "hmac_shared_secret_1": {
          "type": "string",
          "description": "Primary HMAC shared secret for anonymous JWT handshake authentication"
        },
        "hmac_shared_secret_2": {
          "type": "string",
          "description": "Secondary HMAC shared secret for key rotation support"
        },
        "oauth_client_id": {
          "type": "string",
          "description": "OAuth Client ID for HopDrive REST API access"
        },
        "oauth_client_secret": {
          "type": "string",
          "description": "OAuth Client Secret for HopDrive REST API access"
        }
      },
      "additionalProperties": false,
      "description": "Customer/organization API configuration including HMAC shared secrets for anonymous JWT authentication and OAuth credentials for HopDrive REST API access.",
      "x-hopdrive-label": "API Configuration",
      "x-hopdrive-writable-at-levels": [
        "organization",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-warning": "Contains sensitive authentication secrets. HMAC secrets and OAuth credentials must be encrypted before storage.",
      "x-hopdrive-category": "integrations"
    },
    "price_estimates": {
      "type": "boolean",
      "description": "Activate/Deactivate the price estimates feature for this client. This will allow the client to see price estimates for their moves before they create them.",
      "x-hopdrive-label": "Activate Price Estimates",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "region",
        "organization",
        "customer",
        "workflowset",
        "raterulegroup"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "move-planning"
    },
    "distance_estimates": {
      "type": "boolean",
      "description": "Activate/Deactivate the distance estimates feature for clients at this level. This will allow the client to see distance estimates for their moves before they create them.",
      "x-hopdrive-label": "Activate Distance Estimates",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "region",
        "organization",
        "customer",
        "workflowset",
        "raterulegroup"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "move-planning"
    },
    "allow_email_notifications": {
      "type": "boolean",
      "description": "Select whether clients at this level prefer their users to receive email notifications when a move has been picked up or delivered successfully. If selected, the user who created the move, as well as an optional list of other users specified during move planning, will receive a notification via email.",
      "x-hopdrive-label": "Move Status Updates",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "organization",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "notifications"
    },
    "allow_triangle_moves": {
      "type": "boolean",
      "description": "Activate/Deactivate the triangle moves feature for clients at this level. Triangle moves allow for price adjustments to a ride-drive-ride bundle of moves.",
      "x-hopdrive-label": "Activate Triangle Moves",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "region",
        "organization",
        "customer",
        "workflowset",
        "raterulegroup"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-warning": "If you do not understand what this feature does, please leave it disabled.",
      "x-hopdrive-category": "move-planning"
    },
    "enable_csv_import": {
      "type": "boolean",
      "description": "Activate/Deactivate the CSV import feature for customers in Portal. When enabled, the customer can import moves via CSV file upload.",
      "x-hopdrive-label": "Enable CSV Import",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "organization",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "move-planning"
    },
    "public_workflowsets": {
      "type": "boolean",
      "description": "LEGACY: Controls whether public workflowsets are publicly visible or not.",
      "x-hopdrive-label": "Show Public Workflowsets",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "region",
        "organization",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "workflows"
    },
    "rideshare_calling_enabled": {
      "type": "boolean",
      "description": "Hopdrive Lite only. This setting will determine whether Lite clients at this level have opted to enable calling rideshares for their drivers.",
      "x-hopdrive-label": "Rideshare Calling Enabled",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "region",
        "organization",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "rideshare"
    },
    "ride_provider_algorithm_key": {
      "type": "string",
      "description": "Select the algorithm when choosing between ride providers. Each algorithm has different selection criteria and logic.",
      "x-hopdrive-label": "Ride Provider Selection Algorithm",
      "x-hopdrive-writable-at-levels": [
        "region",
        "organization",
        "customer",
        "workflowset",
        "raterulegroup",
        "move"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "rideshare"
    },
    "passthrough_rideshare_surges": {
      "type": "boolean",
      "description": "Activate/Deactivate rideshare surge charges for this client. Surge charges are incurred when the actual rideshare cost is higher than anticipated. When active, the client will cover any rideshare surge charges related to their moves. When inactive, HopDrive will cover the extra cost and waive all rideshare surge charges for this client.",
      "x-hopdrive-label": "Surge Charge Accountability",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "organization",
        "customer",
        "raterulegroup"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "pricing"
    },
    "require_third_party_payer": {
      "type": "boolean",
      "description": "Require the client to input a third party payer when creating moves through the portal or API.",
      "x-hopdrive-label": "Require Third Party Payer",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "organization",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "third-party-payer"
    },
    "auto_assign_ref": {
      "type": "boolean",
      "description": "When clients at this level are creating a concierge return move, automatically assign its reference number to the related concierge pickup of this move.",
      "x-hopdrive-label": "Auto-Assign Ref",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "organization",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "reference-number"
    },
    "default_move_type": {
      "type": "string",
      "enum": [
        "concierge",
        "concierge-loaner",
        "one-way",
        "round-trip"
      ],
      "description": "Default move type pre-selected for new moves. Users can change, but this sets initial selection.",
      "x-hopdrive-label": "Default Move Type",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "region",
        "organization",
        "customer",
        "workflowset",
        "raterulegroup"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "move-planning"
    },
    "allowed_move_types": {
      "type": "array",
      "items": {
        "type": "string",
        "enum": [
          "concierge",
          "concierge-loaner",
          "one-way",
          "round-trip"
        ]
      },
      "description": "Choose which move types are allowed for clients at this level in the move planner. More often than not, there is no reason to restrict a client unless they request/require it.",
      "x-hopdrive-label": "Allowed Move Types",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "region",
        "organization",
        "customer",
        "workflowset",
        "raterulegroup"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "move-planning"
    },
    "allowed_templates": {
      "type": "array",
      "items": {
        "type": "string"
      },
      "description": "LEGACY - Allowed template identifiers. Restricts which move templates this customer can use.",
      "x-hopdrive-label": "Allowed Templates",
      "x-hopdrive-writable-at-levels": [
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "move-planning"
    },
    "add_moves_always": {
      "type": "boolean",
      "description": "Bypass the automatic deactivation of move planning. As mentioned in the Move Planning Activation option, move planning will deactivate automatically when a client goes over their move limit. This switch will override that behavior and allow the client to continue creating moves.",
      "x-hopdrive-label": "Bypass Auto-Deactivation",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "organization",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin",
        "dealer-admin",
        "driver"
      ],
      "x-hopdrive-category": "move-planning"
    },
    "add_moves_disabled": {
      "type": "boolean",
      "description": "Activate/Deactivate the move planning feature for this client. This will allow the client to create moves in our portal or API. NOTE: This switch will deactivate automatically when a client goes over their move limit. Please use the Bypass Auto-Deactivation option to override this behavior.",
      "x-hopdrive-label": "Move Planner Activation",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "organization",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "move-planning"
    },
    "cancellation_fee": {
      "type": "number",
      "minimum": 0,
      "maximum": 1000,
      "description": "Amount to charge clients at this level for cancelling a move that has already been started.",
      "x-hopdrive-label": "Cancellation Fee",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "region",
        "organization",
        "customer",
        "raterulegroup"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-warning": "This does not apply to dry-run cancellations.",
      "x-hopdrive-category": "pricing"
    },
    "delay_price_per_minute": {
      "type": "number",
      "minimum": 0,
      "maximum": 1,
      "description": "Amount to charge clients at this level per each minute our driver is delayed. This only applies when we choose to specifically charge clients at this level for the delay. Min: $0.00, Max: $1.00.",
      "x-hopdrive-label": "Delay Price Per Minute",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "region",
        "organization",
        "customer",
        "raterulegroup"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "pricing"
    },
    "dry_run_behavior": {
      "type": "string",
      "enum": [
        "default-off",
        "default-on",
        "forced-off",
        "forced-on"
      ],
      "description": "Choose the behavior around dry run cancellations through move outcome. When a dry run is applied to a move, this will charge the ar cost of a move to the customer for cancellations instead of using the cancellation fee.",
      "x-hopdrive-label": "Dry Run Behavior",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "organization",
        "customer",
        "raterulegroup"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "pricing"
    },
    "fuel_multiplier": {
      "type": "number",
      "minimum": 0,
      "maximum": 3,
      "description": "The multiplier used to calculate the fuel surcharge. This is a multiplier of the base fare. Min: 0.0, Max: 3.0.",
      "x-hopdrive-label": "Fuel Multiplier",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "organization",
        "customer",
        "raterulegroup"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-warning": "Does NOT affect clients with flat rate pricing.",
      "x-hopdrive-category": "pricing"
    },
    "platform_charge_amount": {
      "type": "number",
      "minimum": 0,
      "description": "Platform charge amount applied to moves at this level.",
      "x-hopdrive-label": "Platform Charge Amount",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "organization",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "pricing"
    },
    "default_workflow_set_id": {
      "type": "integer",
      "exclusiveMinimum": 0,
      "description": "The default workflow set is the preferred set of pickup and delivery workflows used for clients at this level.",
      "x-hopdrive-label": "Default Workflow Set",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "workflows"
    },
    "pickup_workflow_id": {
      "type": "integer",
      "exclusiveMinimum": 0,
      "description": "LEGACY: Workflow ID for pickup operations. Defines driver steps during vehicle pickup.",
      "x-hopdrive-label": "Pickup Workflow ID",
      "x-hopdrive-writable-at-levels": [
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "workflows"
    },
    "delivery_workflow_id": {
      "type": "integer",
      "exclusiveMinimum": 0,
      "description": "LEGACY: Workflow ID for delivery operations. Defines driver steps during vehicle delivery.",
      "x-hopdrive-label": "Delivery Workflow ID",
      "x-hopdrive-writable-at-levels": [
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "workflows"
    },
    "default_pickup_workflow_id": {
      "type": "integer",
      "exclusiveMinimum": 0,
      "description": "Default pickup workflow ID. Auto-populated from workflowset selection.",
      "x-hopdrive-label": "Default Pickup Workflow ID",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "workflows"
    },
    "default_delivery_workflow_id": {
      "type": "integer",
      "exclusiveMinimum": 0,
      "description": "Default delivery workflow ID. Auto-populated from workflowset selection.",
      "x-hopdrive-label": "Default Delivery Workflow ID",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "workflows"
    },
    "default_payer": {
      "anyOf": [
        {
          "type": "integer",
          "exclusiveMinimum": 0
        },
        {
          "type": "null"
        }
      ],
      "description": "The default payer for moves created through the portal or API. For now, we have to select this from the full list of customers. Please be sure you have selected the correct customer before you save. To remove the default payer, select 'None'.",
      "x-hopdrive-label": "Default Payer",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "organization",
        "customer",
        "workflowset",
        "raterulegroup"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "third-party-payer"
    },
    "reference_num_label": {
      "type": "string",
      "description": "A client-specific label to display for the reference number field (e.g. \"RO Number\", \"Claim Number\", \"Fleet ID\", etc.).",
      "x-hopdrive-label": "Reference ID Label",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "organization",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "reference-number"
    },
    "reference_num_required_for_pickup": {
      "type": "boolean",
      "description": "Require the reference number to be entered for concierge pickup moves.",
      "x-hopdrive-label": "Ref Required for Concierge Pickup",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "organization",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "reference-number"
    },
    "reference_num_required_for_return": {
      "type": "boolean",
      "description": "Require the reference number to be entered for concierge return moves.",
      "x-hopdrive-label": "Ref Required for Concierge Return",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "organization",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "reference-number"
    },
    "reference_num_report_enabled": {
      "type": "boolean",
      "description": "Enable the missing reference number report feature for clients at this level.",
      "x-hopdrive-label": "Missing Ref Report Enabled",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "organization",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "reference-number"
    },
    "reference_num_report_label": {
      "type": "string",
      "description": "A client-specific label to display for the missing reference number report.",
      "x-hopdrive-label": "Missing Ref Report Label",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "organization",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "reference-number"
    },
    "serviceable_radius_miles": {
      "type": [
        "number",
        "string"
      ],
      "description": "Used to validate move eligibility within the radius for clients who pre-verify using our API. For more information, see our API documentation here.",
      "x-hopdrive-label": "HopDrive Service Radius",
      "x-hopdrive-writable-at-levels": [
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-warning": "If set, must enable pickup or delivery verification.",
      "x-hopdrive-category": "service-radius"
    },
    "customer_serviceable_radius_miles": {
      "type": "number",
      "minimum": 0,
      "description": "Used to validate move eligibility within the radius for clients who use our HopDrive Lite tier. Regions do not apply to these customers as they do not utilize our driver network.",
      "x-hopdrive-label": "Customer Serviceable Radius Miles",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "service-radius"
    },
    "serviceable_radius_by_delivery": {
      "type": "boolean",
      "description": "Toggle on to verify that the delivery address is within the HopDrive Service Radius.",
      "x-hopdrive-label": "Delivery Verification for HopDrive Service Radius",
      "x-hopdrive-writable-at-levels": [
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "service-radius"
    },
    "serviceable_radius_by_pickup": {
      "type": "boolean",
      "description": "Toggle on to verify that the pickup address is within the HopDrive Service Radius.",
      "x-hopdrive-label": "Pickup Verification for HopDrive Service Radius",
      "x-hopdrive-writable-at-levels": [
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "service-radius"
    },
    "ride_type_override": {
      "type": "string",
      "enum": [
        "",
        "lyft",
        "uber",
        "shuttle"
      ],
      "description": "Select the preferred rideshare service for clients at this level. When planning an 'auto' ride, this partner will be selected by default",
      "x-hopdrive-label": "Preferred Ride Partner",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "region",
        "organization",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "rideshare"
    },
    "branding": {
      "type": "object",
      "properties": {
        "active": {
          "type": "boolean",
          "description": "Activate/Deactivate the branding feature for clients at this level. Branded clients will be visible to their customers not as HopDrive, but as the client's own brand."
        },
        "name": {
          "type": "string",
          "description": "Name to be displayed to the client's customers."
        },
        "primary_color": {
          "anyOf": [
            {
              "anyOf": [
                {
                  "not": {}
                },
                {
                  "anyOf": [
                    {
                      "type": "string",
                      "pattern": "^#[0-9A-Fa-f]{6}$",
                      "description": "Primary brand color in hex format (e.g., \"#0d5298\"). Used for core UI elements with emphasis."
                    },
                    {
                      "type": "null"
                    }
                  ],
                  "description": "Primary brand color in hex format (e.g., \"#0d5298\"). Used for core UI elements with emphasis."
                }
              ],
              "description": "Primary brand color in hex format (e.g., \"#0d5298\"). Used for core UI elements with emphasis."
            },
            {
              "type": "string",
              "const": ""
            }
          ],
          "description": "Primary brand color in hex format (e.g., \"#0d5298\"). Used for core UI elements with emphasis."
        },
        "secondary_color": {
          "anyOf": [
            {
              "anyOf": [
                {
                  "not": {}
                },
                {
                  "anyOf": [
                    {
                      "type": "string",
                      "pattern": "^#[0-9A-Fa-f]{6}$",
                      "description": "Secondary/accent brand color in hex format. Used for secondary UI elements and highlights."
                    },
                    {
                      "type": "null"
                    }
                  ],
                  "description": "Secondary/accent brand color in hex format. Used for secondary UI elements and highlights."
                }
              ],
              "description": "Secondary/accent brand color in hex format. Used for secondary UI elements and highlights."
            },
            {
              "type": "string",
              "const": ""
            }
          ],
          "description": "Secondary/accent brand color in hex format. Used for secondary UI elements and highlights."
        },
        "backdrop_color": {
          "anyOf": [
            {
              "anyOf": [
                {
                  "not": {}
                },
                {
                  "anyOf": [
                    {
                      "type": "string",
                      "pattern": "^#[0-9A-Fa-f]{6}$",
                      "description": "Background color for logo area in hex format. Be sure to contrast this to the logo for visibility."
                    },
                    {
                      "type": "null"
                    }
                  ],
                  "description": "Background color for logo area in hex format. Be sure to contrast this to the logo for visibility."
                }
              ],
              "description": "Background color for logo area in hex format. Be sure to contrast this to the logo for visibility."
            },
            {
              "type": "string",
              "const": ""
            }
          ],
          "description": "Background color for logo area in hex format. Be sure to contrast this to the logo for visibility."
        },
        "logo_url": {
          "anyOf": [
            {
              "anyOf": [
                {
                  "not": {}
                },
                {
                  "type": "string",
                  "format": "uri",
                  "description": "The logo is a link to a PNG or SVG file to give clients at this level some imagery to go along with their name & colors. Due to limitations, the maximum file size is 0.5MB/500KB. Please also make sure the image dimensions are at least 40px tall as anything lower will be too low of a resolution. Stored in hopdrive-branding-logos S3 bucket."
                }
              ],
              "description": "The logo is a link to a PNG or SVG file to give clients at this level some imagery to go along with their name & colors. Due to limitations, the maximum file size is 0.5MB/500KB. Please also make sure the image dimensions are at least 40px tall as anything lower will be too low of a resolution. Stored in hopdrive-branding-logos S3 bucket."
            },
            {
              "type": "string",
              "const": ""
            }
          ],
          "description": "The logo is a link to a PNG or SVG file to give clients at this level some imagery to go along with their name & colors. Due to limitations, the maximum file size is 0.5MB/500KB. Please also make sure the image dimensions are at least 40px tall as anything lower will be too low of a resolution. Stored in hopdrive-branding-logos S3 bucket."
        },
        "customer_support_phone_number": {
          "type": "string",
          "description": "Customer support phone for HopDrive Lite tier ONLY. Displayed in concierge SMS to consumers. Not applicable to Standard tier."
        }
      },
      "additionalProperties": false,
      "description": "The branding module is a way for us to white-label our clients. White-labeling is a way to display our clients in a unique way compared to the HopDrive default branding.",
      "x-hopdrive-label": "Branding",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "organization",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-warning": "Only available to organizations. Customer branding inherits from parent org.",
      "x-hopdrive-category": "branding"
    },
    "appraisals": {
      "type": "object",
      "properties": {
        "active": {
          "type": "boolean",
          "description": "Activate/Deactivate the appraisals feature for this client. NOTE: Flipping this switch ON will create an active timeframe starting at the beginning of the month. Flipping this switch OFF will end the current active timeframe at the end of the month."
        },
        "active_timestamps": {
          "type": "array",
          "items": {
            "type": "object",
            "properties": {
              "from": {
                "type": "string",
                "format": "date-time",
                "description": "Start date in ISO 8601 format (e.g., \"2025-01-01T00:00:00Z\")"
              },
              "to": {
                "anyOf": [
                  {
                    "type": "string",
                    "format": "date-time"
                  },
                  {
                    "type": "string",
                    "const": "now"
                  }
                ],
                "description": "End date in ISO 8601 format, or \"now\" to indicate currently active"
              }
            },
            "additionalProperties": false,
            "description": "Timestamp range for appraisal activation. Toggle on sets \"from\" to month start; toggle off sets \"to\" to month end."
          },
          "description": "Active timeframes are a set of start and end timestamps that determine a window of vehicles for a client to appraise. We do this to restrict clients from being able to access moves they didnt pay the appraisals subscription for. NOTE: The appraisals feature must be activated in order for the client to appraise vehicles (even if they have a time window that includes the current date)."
        },
        "allowed_move_types": {
          "type": "array",
          "items": {
            "type": "string",
            "enum": [
              "concierge",
              "operations"
            ]
          },
          "description": "Move types that support appraisals. Only these types will have appraisal functionality. Generally, we should allow all move types unless specified in the client contract."
        },
        "level": {
          "type": "integer",
          "minimum": 0,
          "description": "Appraisal level/tier (0 = default). Higher levels may unlock additional features."
        }
      },
      "additionalProperties": false,
      "description": "The appraisals module is a way for our clients to appraise vehicles right off the lot. This system is powered by the Blackbook API and allows the client to build their own appraisals based on vehicle trim and condition.",
      "x-hopdrive-label": "Appraisals",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "raterulegroup"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-warning": "Requires premium subscription. Check client contract before activating.",
      "x-hopdrive-category": "appraisals"
    },
    "appointments": {
      "type": "object",
      "properties": {
        "enabled": {
          "type": "boolean",
          "description": "LEGACY: Enable/disable the appointments feature for clients at this level. When enabled, the appointments feature will be available in the Portal."
        },
        "ref_num_label": {
          "type": "string",
          "description": "LEGACY: Reference number label within the appointments feature."
        },
        "notificationEmail": {
          "type": "string",
          "format": "email",
          "description": "LEGACY: Email address that receives appointment notifications and alerts."
        }
      },
      "additionalProperties": false,
      "description": "LEGACY: Appointments feature config. For new scheduling, see Appointment Management.",
      "x-hopdrive-label": "Appointments",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "appointments"
    },
    "appointment_management": {
      "type": "object",
      "properties": {
        "enabled": {
          "type": "boolean",
          "description": "Enable or disable appointment management features for clients at this level. When enabled, users can view the timeline in Portal and manage driver schedules. When disabled, all appointment-related functionality will be inactive."
        },
        "full_planning_enabled": {
          "type": "boolean",
          "description": "Enable full planning and time suggestions for appointment management. When enabled, customers can take advantage of all planning features and intelligent time slot recommendations offered by the appointment management system. Note: Appointment Management must be enabled first."
        },
        "strategy": {
          "type": "string",
          "description": "Select the algorithm to use for appointment scheduling. Each algorithm has different scheduling criteria and driver utilization logic."
        },
        "driver_count": {
          "type": "integer",
          "exclusiveMinimum": 0,
          "description": "For Gig customers only: Expected number of drivers typically available for this rooftop. Used by the algorithm to calculate capacity and suggest appropriate appointment times. For SaaS customers, the system reads from their actual drivers."
        },
        "move_buffer_minutes": {
          "type": "integer",
          "minimum": 0,
          "description": "Minimum time gap (in minutes) required between consecutive moves. Prevents back-to-back scheduling that doesn't account for traffic delays, customer interaction time, and vehicle inspection/handoff. Example: 40 minutes ensures drivers have breathing room between appointments."
        },
        "enforcement_mode": {
          "type": "string",
          "enum": [
            "suggestion",
            "forced"
          ],
          "description": "Controls whether suggested times are recommendations or hard constraints. \"Suggestion\" displays suggested times but allows dealer to override. \"Forced\" only allows suggested times to be selectable, preventing submission of non-compliant appointment times."
        },
        "offset_max_minutes": {
          "type": "integer",
          "minimum": 0,
          "description": "Maximum time deviation (in minutes) a dealer can adjust from a suggested appointment time. Controls how far dealers can shift appointments from algorithm-suggested slots. Example: 30-minute offset with 8:00 AM suggested slot allows 7:30 AM (30 minutes earlier), 8:00 AM (suggested time), or 8:30 AM (30 minutes later)."
        },
        "offset_increment_minutes": {
          "type": "integer",
          "description": "Defines the time increment (in minutes) for allowable offset adjustments. Prevents arbitrary times like 8:12 AM or 8:37 AM that complicate scheduling. Works in conjunction with Offset Max to control available override options. Example: 15-minute increments allow 8:00, 8:15, 8:30, 8:45, etc."
        }
      },
      "additionalProperties": false,
      "description": "Configure how appointment times are calculated, presented, and enforced for dealers. Control driver utilization algorithms, time buffers, enforcement modes, and allowable scheduling adjustments.",
      "x-hopdrive-label": "Appointment Management",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "appointment-management"
    },
    "hopdrive_contacts": {
      "type": "object",
      "properties": {
        "account_manager": {
          "anyOf": [
            {
              "type": "integer",
              "description": "Account manager user ID. Primary point of contact for day-to-day account needs."
            },
            {
              "type": "null"
            }
          ],
          "description": "Account manager user ID. Primary point of contact for day-to-day account needs."
        },
        "ar_rep": {
          "anyOf": [
            {
              "type": "integer",
              "description": "Accounts Receivable representative user ID. Handles billing inquiries and payment issues."
            },
            {
              "type": "null"
            }
          ],
          "description": "Accounts Receivable representative user ID. Handles billing inquiries and payment issues."
        },
        "sales_rep": {
          "anyOf": [
            {
              "type": "integer",
              "description": "Sales representative user ID. Primary contact for upselling and account expansion."
            },
            {
              "type": "null"
            }
          ],
          "description": "Sales representative user ID. Primary contact for upselling and account expansion."
        },
        "market_manager": {
          "anyOf": [
            {
              "type": "integer",
              "description": "Market manager user ID. Oversees regional operations and driver relationships."
            },
            {
              "type": "null"
            }
          ],
          "description": "Market manager user ID. Oversees regional operations and driver relationships."
        }
      },
      "additionalProperties": false,
      "description": "HopDrive employee assignments. Internal team members responsible for this account relationship.",
      "x-hopdrive-label": "HopDrive Contacts",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "region",
        "organization",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "hopdrive-contacts"
    },
    "customer_values": {
      "type": "object",
      "properties": {
        "program": {
          "type": "string",
          "description": "Program name identifier (e.g., \"GMA\")"
        },
        "dealer_code": {
          "type": "string",
          "description": "Dealer code identifier for external systems"
        },
        "billing_program": {
          "type": "string",
          "description": "Billing program identifier for invoicing categorization"
        },
        "gma_market": {
          "type": "string",
          "description": "GMA market code (e.g., \"GX4\", \"GC2\")"
        },
        "gma_region": {
          "type": "string",
          "description": "GMA region code (e.g., \"SC\", \"EA\", \"WE\")"
        },
        "gma_program": {
          "type": "string",
          "description": "GMA program name for reporting"
        },
        "gma_dealer_cd": {
          "type": "string",
          "description": "GMA dealer code (e.g., \"TX712\")"
        },
        "gma_retailer_name": {
          "type": "string",
          "description": "GMA retailer display name for reports"
        }
      },
      "additionalProperties": true,
      "description": "Custom key-value data for reporting and integrations. Keys are auto-formatted: lowercase with spaces to underscores.",
      "x-hopdrive-label": "Custom Settings",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "region",
        "organization",
        "customer",
        "workflowset",
        "raterulegroup",
        "move"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "custom-settings"
    },
    "fuel": {
      "type": "object",
      "properties": {
        "fuel_level": {
          "type": "number",
          "minimum": 0,
          "maximum": 1,
          "description": "The expected fuel level at vehicle delivery (in percent)."
        },
        "min_fuel_price": {
          "type": "number",
          "minimum": 0,
          "description": "Minimum fuel spend in dollars. Amounts below this will NOT be authorized. Prevents trivial purchases."
        },
        "max_fuel_price": {
          "type": "number",
          "minimum": 0,
          "description": "Maximum fuel spend in dollars. Amounts above this will NOT be authorized at pump. Prevents excessive purchases."
        },
        "min_fuel_spend": {
          "type": "number",
          "minimum": 0,
          "description": "LEGACY: Alternative minimum fuel spend threshold. DEPRECATED: Use min_fuel_price instead. Still used in fuel-sdk, event-handlers, hasura-migrations. At least one production customer (ID 3) still uses this field. Both min_fuel_spend and min_fuel_price are supported for backwards compatibility."
        },
        "max_fuel_spend": {
          "type": "number",
          "minimum": 0,
          "description": "LEGACY: Alternative maximum fuel spend cap. DEPRECATED: Use max_fuel_price instead. Still used in fuel-sdk, event-handlers, hasura-migrations. Both max_fuel_spend and max_fuel_price are supported for backwards compatibility."
        }
      },
      "additionalProperties": false,
      "description": "Fuel reimbursement and authorization configuration. Controls driver fuel spending limits and required fuel levels.",
      "x-hopdrive-label": "Fuel",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "fuel"
    },
    "fraud_audit": {
      "type": "object",
      "properties": {
        "enabled": {
          "type": "boolean",
          "description": "Turn on fraud audit monitoring to detect potential fraud cases. This will flag any trips that are outside the configured distance and time tolerances."
        },
        "time_tolerance": {
          "type": "integer",
          "minimum": 0,
          "maximum": 120,
          "description": "Maximum allowed time deviation (in minutes) before flagging as potentially fraudulent behavior."
        },
        "distance_tolerance": {
          "type": "integer",
          "minimum": 0,
          "maximum": 50,
          "description": "Maximum allowed distance deviation (in miles) before flagging as potentially fraudulent behavior."
        }
      },
      "additionalProperties": false,
      "description": "Fraud audit settings help detect potential fraud cases. This will flag any trips that are outside the configured distance and time tolerances.",
      "x-hopdrive-label": "Fraud Audit",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "region",
        "organization",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "fraud-audit"
    },
    "dms": {
      "type": "object",
      "properties": {
        "service": {
          "type": "string",
          "description": "DMS service provider name (e.g., \"CDK\", \"Reynolds\", \"DealerTrack\")"
        },
        "props": {
          "type": "object",
          "additionalProperties": {},
          "description": "Additional DMS-specific properties. Structure varies by provider."
        }
      },
      "additionalProperties": false,
      "description": "Dealer Management System integration. Enables automatic data sync with dealership DMS.",
      "x-hopdrive-label": "DMS",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "dms"
    },
    "email_scrape_config": {
      "type": "object",
      "properties": {
        "name": {
          "type": "string",
          "description": "Configuration name for identification."
        },
        "version": {
          "type": "string",
          "description": "Schema version for compatibility tracking."
        },
        "key": {
          "type": "string",
          "description": "Unique key identifier for this configuration."
        },
        "mappings": {
          "type": "object",
          "properties": {
            "name": {
              "type": "object",
              "properties": {
                "selector": {
                  "type": "string",
                  "description": "CSS selector to locate the element containing the field value."
                },
                "pipeline": {
                  "type": "array",
                  "items": {
                    "type": "object",
                    "properties": {
                      "ordinal": {
                        "type": "integer",
                        "minimum": 0,
                        "description": "Execution order (0-indexed). Steps run in ascending order."
                      },
                      "plugin": {
                        "type": "string",
                        "enum": [
                          "trim",
                          "getMetaContent",
                          "matchThenNextElement",
                          "extractBetween"
                        ],
                        "description": "Plugin: trim=whitespace, getMetaContent=meta tag, matchThenNextElement=find+next, extractBetween=markers"
                      },
                      "params": {
                        "type": "object",
                        "additionalProperties": {
                          "type": "string"
                        },
                        "description": "Plugin-specific parameters as key-value pairs."
                      }
                    },
                    "additionalProperties": false,
                    "description": "Single step in email scraping pipeline."
                  },
                  "description": "Ordered processing steps to extract and transform the value."
                }
              },
              "additionalProperties": false,
              "description": "Field mapping for extracting data from emails."
            },
            "phone": {
              "type": "object",
              "properties": {
                "selector": {
                  "type": "string",
                  "description": "CSS selector to locate the element containing the field value."
                },
                "pipeline": {
                  "type": "array",
                  "items": {
                    "type": "object",
                    "properties": {
                      "ordinal": {
                        "type": "integer",
                        "minimum": 0,
                        "description": "Execution order (0-indexed). Steps run in ascending order."
                      },
                      "plugin": {
                        "type": "string",
                        "enum": [
                          "trim",
                          "getMetaContent",
                          "matchThenNextElement",
                          "extractBetween"
                        ],
                        "description": "Plugin: trim=whitespace, getMetaContent=meta tag, matchThenNextElement=find+next, extractBetween=markers"
                      },
                      "params": {
                        "type": "object",
                        "additionalProperties": {
                          "type": "string"
                        },
                        "description": "Plugin-specific parameters as key-value pairs."
                      }
                    },
                    "additionalProperties": false,
                    "description": "Single step in email scraping pipeline."
                  },
                  "description": "Ordered processing steps to extract and transform the value."
                }
              },
              "additionalProperties": false,
              "description": "Field mapping for extracting data from emails."
            },
            "address": {
              "type": "object",
              "properties": {
                "selector": {
                  "type": "string",
                  "description": "CSS selector to locate the element containing the field value."
                },
                "pipeline": {
                  "type": "array",
                  "items": {
                    "type": "object",
                    "properties": {
                      "ordinal": {
                        "type": "integer",
                        "minimum": 0,
                        "description": "Execution order (0-indexed). Steps run in ascending order."
                      },
                      "plugin": {
                        "type": "string",
                        "enum": [
                          "trim",
                          "getMetaContent",
                          "matchThenNextElement",
                          "extractBetween"
                        ],
                        "description": "Plugin: trim=whitespace, getMetaContent=meta tag, matchThenNextElement=find+next, extractBetween=markers"
                      },
                      "params": {
                        "type": "object",
                        "additionalProperties": {
                          "type": "string"
                        },
                        "description": "Plugin-specific parameters as key-value pairs."
                      }
                    },
                    "additionalProperties": false,
                    "description": "Single step in email scraping pipeline."
                  },
                  "description": "Ordered processing steps to extract and transform the value."
                }
              },
              "additionalProperties": false,
              "description": "Field mapping for extracting data from emails."
            },
            "vehicle": {
              "type": "object",
              "properties": {
                "selector": {
                  "type": "string",
                  "description": "CSS selector to locate the element containing the field value."
                },
                "pipeline": {
                  "type": "array",
                  "items": {
                    "type": "object",
                    "properties": {
                      "ordinal": {
                        "type": "integer",
                        "minimum": 0,
                        "description": "Execution order (0-indexed). Steps run in ascending order."
                      },
                      "plugin": {
                        "type": "string",
                        "enum": [
                          "trim",
                          "getMetaContent",
                          "matchThenNextElement",
                          "extractBetween"
                        ],
                        "description": "Plugin: trim=whitespace, getMetaContent=meta tag, matchThenNextElement=find+next, extractBetween=markers"
                      },
                      "params": {
                        "type": "object",
                        "additionalProperties": {
                          "type": "string"
                        },
                        "description": "Plugin-specific parameters as key-value pairs."
                      }
                    },
                    "additionalProperties": false,
                    "description": "Single step in email scraping pipeline."
                  },
                  "description": "Ordered processing steps to extract and transform the value."
                }
              },
              "additionalProperties": false,
              "description": "Field mapping for extracting data from emails."
            },
            "vin": {
              "type": "object",
              "properties": {
                "selector": {
                  "type": "string",
                  "description": "CSS selector to locate the element containing the field value."
                },
                "pipeline": {
                  "type": "array",
                  "items": {
                    "type": "object",
                    "properties": {
                      "ordinal": {
                        "type": "integer",
                        "minimum": 0,
                        "description": "Execution order (0-indexed). Steps run in ascending order."
                      },
                      "plugin": {
                        "type": "string",
                        "enum": [
                          "trim",
                          "getMetaContent",
                          "matchThenNextElement",
                          "extractBetween"
                        ],
                        "description": "Plugin: trim=whitespace, getMetaContent=meta tag, matchThenNextElement=find+next, extractBetween=markers"
                      },
                      "params": {
                        "type": "object",
                        "additionalProperties": {
                          "type": "string"
                        },
                        "description": "Plugin-specific parameters as key-value pairs."
                      }
                    },
                    "additionalProperties": false,
                    "description": "Single step in email scraping pipeline."
                  },
                  "description": "Ordered processing steps to extract and transform the value."
                }
              },
              "additionalProperties": false,
              "description": "Field mapping for extracting data from emails."
            },
            "advisor": {
              "type": "object",
              "properties": {
                "selector": {
                  "type": "string",
                  "description": "CSS selector to locate the element containing the field value."
                },
                "pipeline": {
                  "type": "array",
                  "items": {
                    "type": "object",
                    "properties": {
                      "ordinal": {
                        "type": "integer",
                        "minimum": 0,
                        "description": "Execution order (0-indexed). Steps run in ascending order."
                      },
                      "plugin": {
                        "type": "string",
                        "enum": [
                          "trim",
                          "getMetaContent",
                          "matchThenNextElement",
                          "extractBetween"
                        ],
                        "description": "Plugin: trim=whitespace, getMetaContent=meta tag, matchThenNextElement=find+next, extractBetween=markers"
                      },
                      "params": {
                        "type": "object",
                        "additionalProperties": {
                          "type": "string"
                        },
                        "description": "Plugin-specific parameters as key-value pairs."
                      }
                    },
                    "additionalProperties": false,
                    "description": "Single step in email scraping pipeline."
                  },
                  "description": "Ordered processing steps to extract and transform the value."
                }
              },
              "additionalProperties": false,
              "description": "Field mapping for extracting data from emails."
            },
            "services": {
              "type": "object",
              "properties": {
                "selector": {
                  "type": "string",
                  "description": "CSS selector to locate the element containing the field value."
                },
                "pipeline": {
                  "type": "array",
                  "items": {
                    "type": "object",
                    "properties": {
                      "ordinal": {
                        "type": "integer",
                        "minimum": 0,
                        "description": "Execution order (0-indexed). Steps run in ascending order."
                      },
                      "plugin": {
                        "type": "string",
                        "enum": [
                          "trim",
                          "getMetaContent",
                          "matchThenNextElement",
                          "extractBetween"
                        ],
                        "description": "Plugin: trim=whitespace, getMetaContent=meta tag, matchThenNextElement=find+next, extractBetween=markers"
                      },
                      "params": {
                        "type": "object",
                        "additionalProperties": {
                          "type": "string"
                        },
                        "description": "Plugin-specific parameters as key-value pairs."
                      }
                    },
                    "additionalProperties": false,
                    "description": "Single step in email scraping pipeline."
                  },
                  "description": "Ordered processing steps to extract and transform the value."
                }
              },
              "additionalProperties": false,
              "description": "Field mapping for extracting data from emails."
            },
            "dealer_code": {
              "type": "object",
              "properties": {
                "selector": {
                  "type": "string",
                  "description": "CSS selector to locate the element containing the field value."
                },
                "pipeline": {
                  "type": "array",
                  "items": {
                    "type": "object",
                    "properties": {
                      "ordinal": {
                        "type": "integer",
                        "minimum": 0,
                        "description": "Execution order (0-indexed). Steps run in ascending order."
                      },
                      "plugin": {
                        "type": "string",
                        "enum": [
                          "trim",
                          "getMetaContent",
                          "matchThenNextElement",
                          "extractBetween"
                        ],
                        "description": "Plugin: trim=whitespace, getMetaContent=meta tag, matchThenNextElement=find+next, extractBetween=markers"
                      },
                      "params": {
                        "type": "object",
                        "additionalProperties": {
                          "type": "string"
                        },
                        "description": "Plugin-specific parameters as key-value pairs."
                      }
                    },
                    "additionalProperties": false,
                    "description": "Single step in email scraping pipeline."
                  },
                  "description": "Ordered processing steps to extract and transform the value."
                }
              },
              "additionalProperties": false,
              "description": "Field mapping for extracting data from emails."
            },
            "appointment_time": {
              "type": "object",
              "properties": {
                "selector": {
                  "type": "string",
                  "description": "CSS selector to locate the element containing the field value."
                },
                "pipeline": {
                  "type": "array",
                  "items": {
                    "type": "object",
                    "properties": {
                      "ordinal": {
                        "type": "integer",
                        "minimum": 0,
                        "description": "Execution order (0-indexed). Steps run in ascending order."
                      },
                      "plugin": {
                        "type": "string",
                        "enum": [
                          "trim",
                          "getMetaContent",
                          "matchThenNextElement",
                          "extractBetween"
                        ],
                        "description": "Plugin: trim=whitespace, getMetaContent=meta tag, matchThenNextElement=find+next, extractBetween=markers"
                      },
                      "params": {
                        "type": "object",
                        "additionalProperties": {
                          "type": "string"
                        },
                        "description": "Plugin-specific parameters as key-value pairs."
                      }
                    },
                    "additionalProperties": false,
                    "description": "Single step in email scraping pipeline."
                  },
                  "description": "Ordered processing steps to extract and transform the value."
                }
              },
              "additionalProperties": false,
              "description": "Field mapping for extracting data from emails."
            },
            "appointment_type": {
              "type": "object",
              "properties": {
                "selector": {
                  "type": "string",
                  "description": "CSS selector to locate the element containing the field value."
                },
                "pipeline": {
                  "type": "array",
                  "items": {
                    "type": "object",
                    "properties": {
                      "ordinal": {
                        "type": "integer",
                        "minimum": 0,
                        "description": "Execution order (0-indexed). Steps run in ascending order."
                      },
                      "plugin": {
                        "type": "string",
                        "enum": [
                          "trim",
                          "getMetaContent",
                          "matchThenNextElement",
                          "extractBetween"
                        ],
                        "description": "Plugin: trim=whitespace, getMetaContent=meta tag, matchThenNextElement=find+next, extractBetween=markers"
                      },
                      "params": {
                        "type": "object",
                        "additionalProperties": {
                          "type": "string"
                        },
                        "description": "Plugin-specific parameters as key-value pairs."
                      }
                    },
                    "additionalProperties": false,
                    "description": "Single step in email scraping pipeline."
                  },
                  "description": "Ordered processing steps to extract and transform the value."
                }
              },
              "additionalProperties": false,
              "description": "Field mapping for extracting data from emails."
            },
            "arrangement": {
              "type": "object",
              "properties": {
                "selector": {
                  "type": "string",
                  "description": "CSS selector to locate the element containing the field value."
                },
                "pipeline": {
                  "type": "array",
                  "items": {
                    "type": "object",
                    "properties": {
                      "ordinal": {
                        "type": "integer",
                        "minimum": 0,
                        "description": "Execution order (0-indexed). Steps run in ascending order."
                      },
                      "plugin": {
                        "type": "string",
                        "enum": [
                          "trim",
                          "getMetaContent",
                          "matchThenNextElement",
                          "extractBetween"
                        ],
                        "description": "Plugin: trim=whitespace, getMetaContent=meta tag, matchThenNextElement=find+next, extractBetween=markers"
                      },
                      "params": {
                        "type": "object",
                        "additionalProperties": {
                          "type": "string"
                        },
                        "description": "Plugin-specific parameters as key-value pairs."
                      }
                    },
                    "additionalProperties": false,
                    "description": "Single step in email scraping pipeline."
                  },
                  "description": "Ordered processing steps to extract and transform the value."
                }
              },
              "additionalProperties": false,
              "description": "Field mapping for extracting data from emails."
            },
            "confirmation_code": {
              "type": "object",
              "properties": {
                "selector": {
                  "type": "string",
                  "description": "CSS selector to locate the element containing the field value."
                },
                "pipeline": {
                  "type": "array",
                  "items": {
                    "type": "object",
                    "properties": {
                      "ordinal": {
                        "type": "integer",
                        "minimum": 0,
                        "description": "Execution order (0-indexed). Steps run in ascending order."
                      },
                      "plugin": {
                        "type": "string",
                        "enum": [
                          "trim",
                          "getMetaContent",
                          "matchThenNextElement",
                          "extractBetween"
                        ],
                        "description": "Plugin: trim=whitespace, getMetaContent=meta tag, matchThenNextElement=find+next, extractBetween=markers"
                      },
                      "params": {
                        "type": "object",
                        "additionalProperties": {
                          "type": "string"
                        },
                        "description": "Plugin-specific parameters as key-value pairs."
                      }
                    },
                    "additionalProperties": false,
                    "description": "Single step in email scraping pipeline."
                  },
                  "description": "Ordered processing steps to extract and transform the value."
                }
              },
              "additionalProperties": false,
              "description": "Field mapping for extracting data from emails."
            }
          },
          "additionalProperties": false,
          "description": "Field extraction mappings for appointment email data."
        }
      },
      "additionalProperties": false,
      "description": "Email scraping configuration for automated data extraction.",
      "x-hopdrive-label": "Email Scrape Config",
      "x-hopdrive-writable-at-levels": [
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "email-scraping"
    },
    "rc_config": {
      "type": "object",
      "properties": {
        "advisor_email": {
          "type": "string",
          "format": "email",
          "description": "Advisor email for automation scenarios."
        },
        "advisor_name": {
          "type": "string",
          "description": "Advisor display name for automation."
        },
        "advisor_phone": {
          "type": "string",
          "description": "Advisor phone for automation."
        },
        "advisor_otp_secret": {
          "type": "string",
          "description": "OTP secret for advisor authentication in automation."
        },
        "driver_email": {
          "type": "string",
          "format": "email",
          "description": "Driver email for automation scenarios."
        },
        "driver_name": {
          "type": "string",
          "description": "Driver display name for automation."
        },
        "driver_phone": {
          "type": "string",
          "description": "Driver phone for automation."
        },
        "email_scrape_config": {
          "type": "object",
          "properties": {
            "name": {
              "type": "string"
            },
            "version": {
              "type": "string"
            },
            "mappings": {
              "type": "object",
              "properties": {
                "tempPass": {
                  "type": "object",
                  "properties": {
                    "selector": {
                      "type": "string",
                      "description": "CSS selector to locate the element containing the field value."
                    },
                    "pipeline": {
                      "type": "array",
                      "items": {
                        "type": "object",
                        "properties": {
                          "ordinal": {
                            "type": "integer",
                            "minimum": 0,
                            "description": "Execution order (0-indexed). Steps run in ascending order."
                          },
                          "plugin": {
                            "type": "string",
                            "enum": [
                              "trim",
                              "getMetaContent",
                              "matchThenNextElement",
                              "extractBetween"
                            ],
                            "description": "Plugin: trim=whitespace, getMetaContent=meta tag, matchThenNextElement=find+next, extractBetween=markers"
                          },
                          "params": {
                            "type": "object",
                            "additionalProperties": {
                              "type": "string"
                            },
                            "description": "Plugin-specific parameters as key-value pairs."
                          }
                        },
                        "additionalProperties": false,
                        "description": "Single step in email scraping pipeline."
                      },
                      "description": "Ordered processing steps to extract and transform the value."
                    }
                  },
                  "additionalProperties": false,
                  "description": "Field mapping for extracting data from emails."
                }
              },
              "additionalProperties": false
            }
          },
          "additionalProperties": false,
          "description": "Email scraping config for extracting temporary passwords in automation."
        },
        "rc_dealer_admin_config_id": {
          "type": "integer",
          "description": "Reference to RC dealer admin configuration record."
        }
      },
      "additionalProperties": false,
      "description": "Remote control/automation configuration for QA testing.",
      "x-hopdrive-label": "RC Config",
      "x-hopdrive-writable-at-levels": [
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "email-scraping"
    },
    "private": {
      "type": "object",
      "additionalProperties": {},
      "description": "Private/internal config. Freeform structure with dynamic boolean toggles for data sharing.",
      "x-hopdrive-label": "Private",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "region",
        "organization",
        "customer",
        "workflowset",
        "raterulegroup",
        "move"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "private"
    },
    "routing": {
      "type": "object",
      "properties": {
        "avoid_tolls": {
          "type": "boolean",
          "description": "When true, routes will avoid toll roads where possible. This may result in longer travel times or distances."
        },
        "avoid_highways": {
          "type": "boolean",
          "description": "When true, routes will avoid highways where possible. Useful for vehicles with highway restrictions or customers preferring local roads."
        },
        "avoid_ferries": {
          "type": "boolean",
          "description": "When true, routes will avoid ferries where possible. Useful for customers who cannot use ferry routes."
        },
        "route_preference": {
          "type": "string",
          "enum": [
            "fastest",
            "shortest"
          ],
          "description": "Route optimization preference. 'fastest' optimizes for travel time (default), 'shortest' optimizes for distance."
        }
      },
      "additionalProperties": false,
      "description": "Routing preferences for lane enrichment. Controls toll avoidance, highway avoidance, ferry avoidance, and route optimization preferences.",
      "x-hopdrive-label": "Routing",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "region",
        "organization",
        "customer",
        "workflowset",
        "raterulegroup"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "move-planning"
    },
    "driver_pipeline": {
      "type": "object",
      "properties": {
        "funnel_open": {
          "type": "boolean",
          "description": "Whether the driver recruitment funnel is open for this region. When true, new drivers can be recruited."
        },
        "active_driver_target": {
          "type": "integer",
          "minimum": 0,
          "description": "Target number of active drivers for this region. Used to determine recruitment goals."
        },
        "waitlist_target": {
          "type": "integer",
          "minimum": 0,
          "description": "Target number of drivers on the waitlist for this region. Maintains a pipeline of ready drivers."
        },
        "backlog_target": {
          "type": "integer",
          "minimum": 0,
          "description": "Maximum number of drivers allowed in the backlog. When reached, recruitment funnel may be closed."
        },
        "engagement_frequency_days": {
          "type": "integer",
          "exclusiveMinimum": 0,
          "description": "How often (in days) to engage with drivers in the pipeline. Used for scheduling follow-up communications."
        },
        "max_missed_confirmations": {
          "type": "integer",
          "minimum": 0,
          "description": "Maximum number of missed confirmations allowed before removing a driver from the recruitment pipeline."
        }
      },
      "additionalProperties": false,
      "description": "Driver recruitment pipeline configuration with funnel management and engagement settings.",
      "x-hopdrive-label": "Driver Pipeline",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "region"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "region-pipeline"
    },
    "connector_triggers": {
      "type": "object",
      "properties": {
        "enabled": {
          "type": "boolean",
          "description": "Master enable/disable toggle for the entire integration"
        },
        "secrets": {
          "type": "object",
          "additionalProperties": {
            "type": "string"
          },
          "default": {},
          "description": "Encrypted secrets storage. Keys are secret names (e.g., \"DEALERWARE_CLIENT_ID\"). Values are encrypted strings (e.g., \"encrypted:v1:abc123...\")"
        },
        "authProviders": {
          "type": "object",
          "additionalProperties": {
            "anyOf": [
              {
                "type": "object",
                "properties": {
                  "type": {
                    "type": "string",
                    "const": "oauth2_client_credentials"
                  },
                  "tokenEndpoint": {
                    "type": "string",
                    "format": "uri",
                    "description": "OAuth token endpoint URL"
                  },
                  "clientId": {
                    "type": "string",
                    "description": "Client ID - can be a secret reference like {SECRET_NAME}"
                  },
                  "clientSecret": {
                    "type": "string",
                    "description": "Client secret - can be a secret reference like {SECRET_NAME}"
                  },
                  "audience": {
                    "type": "string",
                    "description": "OAuth audience (optional)"
                  },
                  "scope": {
                    "type": "string",
                    "description": "OAuth scope (optional)"
                  },
                  "cacheTtlSeconds": {
                    "type": "integer",
                    "exclusiveMinimum": 0,
                    "default": 3540,
                    "description": "Token cache TTL in seconds (default: 3540 = 59 minutes)"
                  }
                },
                "required": [
                  "type",
                  "tokenEndpoint",
                  "clientId",
                  "clientSecret"
                ],
                "additionalProperties": false
              },
              {
                "type": "object",
                "properties": {
                  "type": {
                    "type": "string",
                    "const": "api_key"
                  },
                  "headerName": {
                    "type": "string",
                    "description": "Header name for the API key"
                  },
                  "apiKey": {
                    "type": "string",
                    "description": "API key value - can be a secret reference like {SECRET_NAME}"
                  }
                },
                "required": [
                  "type",
                  "headerName",
                  "apiKey"
                ],
                "additionalProperties": false
              },
              {
                "type": "object",
                "properties": {
                  "type": {
                    "type": "string",
                    "const": "basic"
                  },
                  "username": {
                    "type": "string",
                    "description": "Username - can be a secret reference"
                  },
                  "password": {
                    "type": "string",
                    "description": "Password - can be a secret reference"
                  }
                },
                "required": [
                  "type",
                  "username",
                  "password"
                ],
                "additionalProperties": false
              }
            ]
          },
          "default": {},
          "description": "Auth provider configurations. Keys are provider names referenced by triggers (e.g., \"dealerware-oauth\")"
        },
        "autocompleteTriggers": {
          "type": "object",
          "additionalProperties": {
            "type": "object",
            "properties": {
              "enabled": {
                "type": "boolean",
                "description": "Whether this trigger is enabled"
              },
              "authProvider": {
                "type": "string",
                "description": "Which auth provider to use (references key in authProviders)"
              },
              "httpMethod": {
                "type": "string",
                "enum": [
                  "GET",
                  "POST"
                ],
                "description": "HTTP method for the API call"
              },
              "endpoint": {
                "type": "string",
                "description": "API endpoint URL - supports {search_query} interpolation"
              },
              "requestBody": {
                "type": "string",
                "description": "Request body template for POST requests (optional)"
              },
              "headers": {
                "type": "object",
                "additionalProperties": {
                  "type": "string"
                },
                "description": "Optional custom headers to include with the request - supports template variable interpolation (e.g., { \"api-version\": \"{cdi.api_version}\" })",
                "default": {}
              },
              "minChars": {
                "type": "integer",
                "minimum": 1,
                "default": 3,
                "description": "Minimum characters before triggering autocomplete"
              },
              "debounceMs": {
                "type": "integer",
                "minimum": 0,
                "default": 300,
                "description": "Debounce delay in milliseconds"
              },
              "maxResults": {
                "type": "integer",
                "minimum": 1,
                "maximum": 50,
                "default": 10,
                "description": "Maximum results to return"
              },
              "mappings": {
                "type": "object",
                "properties": {
                  "id": {
                    "type": "object",
                    "properties": {
                      "source": {
                        "type": "string",
                        "description": "JSONPath expression to extract value from API response"
                      },
                      "transforms": {
                        "type": "array",
                        "items": {
                          "anyOf": [
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "uppercase"
                                }
                              },
                              "required": [
                                "type"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "lowercase"
                                }
                              },
                              "required": [
                                "type"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "trim"
                                }
                              },
                              "required": [
                                "type"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "regex"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "pattern": {
                                      "type": "string",
                                      "description": "Regular expression pattern to match"
                                    },
                                    "replacement": {
                                      "type": "string",
                                      "default": "",
                                      "description": "Replacement string for regex matches"
                                    }
                                  },
                                  "required": [
                                    "pattern"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "template"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "template": {
                                      "type": "string",
                                      "description": "Template string supporting {value} and {fieldName} placeholders"
                                    }
                                  },
                                  "required": [
                                    "template"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "split"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "delimiter": {
                                      "type": "string",
                                      "description": "String delimiter to split on"
                                    },
                                    "index": {
                                      "type": "integer",
                                      "minimum": 0,
                                      "description": "Index of the split segment to extract"
                                    }
                                  },
                                  "required": [
                                    "delimiter",
                                    "index"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "concat"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "prefix": {
                                      "type": "string",
                                      "default": "",
                                      "description": "Prefix string to prepend"
                                    },
                                    "suffix": {
                                      "type": "string",
                                      "default": "",
                                      "description": "Suffix string to append"
                                    }
                                  },
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "default"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "value": {
                                      "type": "string",
                                      "description": "Default value to use if field is empty"
                                    }
                                  },
                                  "required": [
                                    "value"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "date_format"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "inputFormat": {
                                      "type": "string",
                                      "description": "Input date format (optional, auto-detected if not provided)"
                                    },
                                    "outputFormat": {
                                      "type": "string",
                                      "description": "Output date format string"
                                    }
                                  },
                                  "required": [
                                    "outputFormat"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            }
                          ]
                        },
                        "description": "Optional chain of transforms to apply to the extracted value"
                      }
                    },
                    "required": [
                      "source"
                    ],
                    "additionalProperties": false
                  },
                  "displayValue": {
                    "type": "object",
                    "properties": {
                      "source": {
                        "type": "string",
                        "description": "JSONPath expression to extract value from API response"
                      },
                      "transforms": {
                        "type": "array",
                        "items": {
                          "anyOf": [
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "uppercase"
                                }
                              },
                              "required": [
                                "type"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "lowercase"
                                }
                              },
                              "required": [
                                "type"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "trim"
                                }
                              },
                              "required": [
                                "type"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "regex"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "pattern": {
                                      "type": "string",
                                      "description": "Regular expression pattern to match"
                                    },
                                    "replacement": {
                                      "type": "string",
                                      "default": "",
                                      "description": "Replacement string for regex matches"
                                    }
                                  },
                                  "required": [
                                    "pattern"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "template"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "template": {
                                      "type": "string",
                                      "description": "Template string supporting {value} and {fieldName} placeholders"
                                    }
                                  },
                                  "required": [
                                    "template"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "split"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "delimiter": {
                                      "type": "string",
                                      "description": "String delimiter to split on"
                                    },
                                    "index": {
                                      "type": "integer",
                                      "minimum": 0,
                                      "description": "Index of the split segment to extract"
                                    }
                                  },
                                  "required": [
                                    "delimiter",
                                    "index"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "concat"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "prefix": {
                                      "type": "string",
                                      "default": "",
                                      "description": "Prefix string to prepend"
                                    },
                                    "suffix": {
                                      "type": "string",
                                      "default": "",
                                      "description": "Suffix string to append"
                                    }
                                  },
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "default"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "value": {
                                      "type": "string",
                                      "description": "Default value to use if field is empty"
                                    }
                                  },
                                  "required": [
                                    "value"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "date_format"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "inputFormat": {
                                      "type": "string",
                                      "description": "Input date format (optional, auto-detected if not provided)"
                                    },
                                    "outputFormat": {
                                      "type": "string",
                                      "description": "Output date format string"
                                    }
                                  },
                                  "required": [
                                    "outputFormat"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            }
                          ]
                        },
                        "description": "Optional chain of transforms to apply to the extracted value"
                      }
                    },
                    "required": [
                      "source"
                    ],
                    "additionalProperties": false
                  },
                  "subtext": {
                    "type": "object",
                    "properties": {
                      "source": {
                        "type": "string",
                        "description": "JSONPath expression to extract value from API response"
                      },
                      "transforms": {
                        "type": "array",
                        "items": {
                          "anyOf": [
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "uppercase"
                                }
                              },
                              "required": [
                                "type"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "lowercase"
                                }
                              },
                              "required": [
                                "type"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "trim"
                                }
                              },
                              "required": [
                                "type"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "regex"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "pattern": {
                                      "type": "string",
                                      "description": "Regular expression pattern to match"
                                    },
                                    "replacement": {
                                      "type": "string",
                                      "default": "",
                                      "description": "Replacement string for regex matches"
                                    }
                                  },
                                  "required": [
                                    "pattern"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "template"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "template": {
                                      "type": "string",
                                      "description": "Template string supporting {value} and {fieldName} placeholders"
                                    }
                                  },
                                  "required": [
                                    "template"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "split"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "delimiter": {
                                      "type": "string",
                                      "description": "String delimiter to split on"
                                    },
                                    "index": {
                                      "type": "integer",
                                      "minimum": 0,
                                      "description": "Index of the split segment to extract"
                                    }
                                  },
                                  "required": [
                                    "delimiter",
                                    "index"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "concat"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "prefix": {
                                      "type": "string",
                                      "default": "",
                                      "description": "Prefix string to prepend"
                                    },
                                    "suffix": {
                                      "type": "string",
                                      "default": "",
                                      "description": "Suffix string to append"
                                    }
                                  },
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "default"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "value": {
                                      "type": "string",
                                      "description": "Default value to use if field is empty"
                                    }
                                  },
                                  "required": [
                                    "value"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "date_format"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "inputFormat": {
                                      "type": "string",
                                      "description": "Input date format (optional, auto-detected if not provided)"
                                    },
                                    "outputFormat": {
                                      "type": "string",
                                      "description": "Output date format string"
                                    }
                                  },
                                  "required": [
                                    "outputFormat"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            }
                          ]
                        },
                        "description": "Optional chain of transforms to apply to the extracted value"
                      }
                    },
                    "required": [
                      "source"
                    ],
                    "additionalProperties": false
                  }
                },
                "required": [
                  "id",
                  "displayValue"
                ],
                "additionalProperties": false,
                "description": "Field mappings for autocomplete output"
              }
            },
            "required": [
              "enabled",
              "authProvider",
              "httpMethod",
              "endpoint",
              "mappings"
            ],
            "additionalProperties": false
          },
          "default": {},
          "description": "Autocomplete trigger configurations. Keys are trigger types matching form field triggers: consumer-name, consumer-phone, reference-number, consumer-vin, loaner-vin, stock-number"
        },
        "fetchTriggers": {
          "type": "object",
          "additionalProperties": {
            "type": "object",
            "properties": {
              "enabled": {
                "type": "boolean",
                "description": "Whether this trigger is enabled"
              },
              "authProvider": {
                "type": "string",
                "description": "Which auth provider to use (references key in authProviders)"
              },
              "httpMethod": {
                "type": "string",
                "enum": [
                  "GET",
                  "POST",
                  "PUT",
                  "PATCH"
                ],
                "description": "HTTP method for the API call"
              },
              "endpoint": {
                "type": "string",
                "description": "API endpoint URL - supports {lookup_value} interpolation"
              },
              "requestBody": {
                "type": "string",
                "description": "Request body template for POST/PUT/PATCH requests (optional)"
              },
              "headers": {
                "type": "object",
                "additionalProperties": {
                  "type": "string"
                },
                "description": "Optional custom headers to include with the request - supports template variable interpolation (e.g., { \"api-version\": \"{cdi.api_version}\" })",
                "default": {}
              },
              "overwriteExisting": {
                "type": "boolean",
                "default": false,
                "description": "Whether fetch results should overwrite existing form field values. When true, all mapped fields from the fetch response will replace existing values. When false (default), only empty form fields will be populated."
              },
              "mappings": {
                "type": "object",
                "properties": {
                  "id": {
                    "type": "object",
                    "properties": {
                      "source": {
                        "type": "string",
                        "description": "JSONPath expression to extract value from API response"
                      },
                      "transforms": {
                        "type": "array",
                        "items": {
                          "anyOf": [
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "uppercase"
                                }
                              },
                              "required": [
                                "type"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "lowercase"
                                }
                              },
                              "required": [
                                "type"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "trim"
                                }
                              },
                              "required": [
                                "type"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "regex"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "pattern": {
                                      "type": "string",
                                      "description": "Regular expression pattern to match"
                                    },
                                    "replacement": {
                                      "type": "string",
                                      "default": "",
                                      "description": "Replacement string for regex matches"
                                    }
                                  },
                                  "required": [
                                    "pattern"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "template"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "template": {
                                      "type": "string",
                                      "description": "Template string supporting {value} and {fieldName} placeholders"
                                    }
                                  },
                                  "required": [
                                    "template"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "split"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "delimiter": {
                                      "type": "string",
                                      "description": "String delimiter to split on"
                                    },
                                    "index": {
                                      "type": "integer",
                                      "minimum": 0,
                                      "description": "Index of the split segment to extract"
                                    }
                                  },
                                  "required": [
                                    "delimiter",
                                    "index"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "concat"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "prefix": {
                                      "type": "string",
                                      "default": "",
                                      "description": "Prefix string to prepend"
                                    },
                                    "suffix": {
                                      "type": "string",
                                      "default": "",
                                      "description": "Suffix string to append"
                                    }
                                  },
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "default"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "value": {
                                      "type": "string",
                                      "description": "Default value to use if field is empty"
                                    }
                                  },
                                  "required": [
                                    "value"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "date_format"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "inputFormat": {
                                      "type": "string",
                                      "description": "Input date format (optional, auto-detected if not provided)"
                                    },
                                    "outputFormat": {
                                      "type": "string",
                                      "description": "Output date format string"
                                    }
                                  },
                                  "required": [
                                    "outputFormat"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            }
                          ]
                        },
                        "description": "Optional chain of transforms to apply to the extracted value"
                      }
                    },
                    "required": [
                      "source"
                    ],
                    "additionalProperties": false
                  },
                  "displayLabel": {
                    "type": "object",
                    "properties": {
                      "source": {
                        "type": "string",
                        "description": "JSONPath expression to extract value from API response"
                      },
                      "transforms": {
                        "type": "array",
                        "items": {
                          "anyOf": [
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "uppercase"
                                }
                              },
                              "required": [
                                "type"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "lowercase"
                                }
                              },
                              "required": [
                                "type"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "trim"
                                }
                              },
                              "required": [
                                "type"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "regex"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "pattern": {
                                      "type": "string",
                                      "description": "Regular expression pattern to match"
                                    },
                                    "replacement": {
                                      "type": "string",
                                      "default": "",
                                      "description": "Replacement string for regex matches"
                                    }
                                  },
                                  "required": [
                                    "pattern"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "template"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "template": {
                                      "type": "string",
                                      "description": "Template string supporting {value} and {fieldName} placeholders"
                                    }
                                  },
                                  "required": [
                                    "template"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "split"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "delimiter": {
                                      "type": "string",
                                      "description": "String delimiter to split on"
                                    },
                                    "index": {
                                      "type": "integer",
                                      "minimum": 0,
                                      "description": "Index of the split segment to extract"
                                    }
                                  },
                                  "required": [
                                    "delimiter",
                                    "index"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "concat"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "prefix": {
                                      "type": "string",
                                      "default": "",
                                      "description": "Prefix string to prepend"
                                    },
                                    "suffix": {
                                      "type": "string",
                                      "default": "",
                                      "description": "Suffix string to append"
                                    }
                                  },
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "default"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "value": {
                                      "type": "string",
                                      "description": "Default value to use if field is empty"
                                    }
                                  },
                                  "required": [
                                    "value"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "date_format"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "inputFormat": {
                                      "type": "string",
                                      "description": "Input date format (optional, auto-detected if not provided)"
                                    },
                                    "outputFormat": {
                                      "type": "string",
                                      "description": "Output date format string"
                                    }
                                  },
                                  "required": [
                                    "outputFormat"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            }
                          ]
                        },
                        "description": "Optional chain of transforms to apply to the extracted value"
                      }
                    },
                    "required": [
                      "source"
                    ],
                    "additionalProperties": false
                  },
                  "referenceNumber": {
                    "type": "object",
                    "properties": {
                      "source": {
                        "type": "string",
                        "description": "JSONPath expression to extract value from API response"
                      },
                      "transforms": {
                        "type": "array",
                        "items": {
                          "anyOf": [
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "uppercase"
                                }
                              },
                              "required": [
                                "type"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "lowercase"
                                }
                              },
                              "required": [
                                "type"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "trim"
                                }
                              },
                              "required": [
                                "type"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "regex"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "pattern": {
                                      "type": "string",
                                      "description": "Regular expression pattern to match"
                                    },
                                    "replacement": {
                                      "type": "string",
                                      "default": "",
                                      "description": "Replacement string for regex matches"
                                    }
                                  },
                                  "required": [
                                    "pattern"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "template"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "template": {
                                      "type": "string",
                                      "description": "Template string supporting {value} and {fieldName} placeholders"
                                    }
                                  },
                                  "required": [
                                    "template"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "split"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "delimiter": {
                                      "type": "string",
                                      "description": "String delimiter to split on"
                                    },
                                    "index": {
                                      "type": "integer",
                                      "minimum": 0,
                                      "description": "Index of the split segment to extract"
                                    }
                                  },
                                  "required": [
                                    "delimiter",
                                    "index"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "concat"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "prefix": {
                                      "type": "string",
                                      "default": "",
                                      "description": "Prefix string to prepend"
                                    },
                                    "suffix": {
                                      "type": "string",
                                      "default": "",
                                      "description": "Suffix string to append"
                                    }
                                  },
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "default"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "value": {
                                      "type": "string",
                                      "description": "Default value to use if field is empty"
                                    }
                                  },
                                  "required": [
                                    "value"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "date_format"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "inputFormat": {
                                      "type": "string",
                                      "description": "Input date format (optional, auto-detected if not provided)"
                                    },
                                    "outputFormat": {
                                      "type": "string",
                                      "description": "Output date format string"
                                    }
                                  },
                                  "required": [
                                    "outputFormat"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            }
                          ]
                        },
                        "description": "Optional chain of transforms to apply to the extracted value"
                      }
                    },
                    "required": [
                      "source"
                    ],
                    "additionalProperties": false
                  },
                  "customerName": {
                    "type": "object",
                    "properties": {
                      "source": {
                        "type": "string",
                        "description": "JSONPath expression to extract value from API response"
                      },
                      "transforms": {
                        "type": "array",
                        "items": {
                          "anyOf": [
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "uppercase"
                                }
                              },
                              "required": [
                                "type"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "lowercase"
                                }
                              },
                              "required": [
                                "type"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "trim"
                                }
                              },
                              "required": [
                                "type"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "regex"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "pattern": {
                                      "type": "string",
                                      "description": "Regular expression pattern to match"
                                    },
                                    "replacement": {
                                      "type": "string",
                                      "default": "",
                                      "description": "Replacement string for regex matches"
                                    }
                                  },
                                  "required": [
                                    "pattern"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "template"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "template": {
                                      "type": "string",
                                      "description": "Template string supporting {value} and {fieldName} placeholders"
                                    }
                                  },
                                  "required": [
                                    "template"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "split"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "delimiter": {
                                      "type": "string",
                                      "description": "String delimiter to split on"
                                    },
                                    "index": {
                                      "type": "integer",
                                      "minimum": 0,
                                      "description": "Index of the split segment to extract"
                                    }
                                  },
                                  "required": [
                                    "delimiter",
                                    "index"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "concat"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "prefix": {
                                      "type": "string",
                                      "default": "",
                                      "description": "Prefix string to prepend"
                                    },
                                    "suffix": {
                                      "type": "string",
                                      "default": "",
                                      "description": "Suffix string to append"
                                    }
                                  },
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "default"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "value": {
                                      "type": "string",
                                      "description": "Default value to use if field is empty"
                                    }
                                  },
                                  "required": [
                                    "value"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "date_format"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "inputFormat": {
                                      "type": "string",
                                      "description": "Input date format (optional, auto-detected if not provided)"
                                    },
                                    "outputFormat": {
                                      "type": "string",
                                      "description": "Output date format string"
                                    }
                                  },
                                  "required": [
                                    "outputFormat"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            }
                          ]
                        },
                        "description": "Optional chain of transforms to apply to the extracted value"
                      }
                    },
                    "required": [
                      "source"
                    ],
                    "additionalProperties": false
                  },
                  "customerPhone": {
                    "type": "object",
                    "properties": {
                      "source": {
                        "type": "string",
                        "description": "JSONPath expression to extract value from API response"
                      },
                      "transforms": {
                        "type": "array",
                        "items": {
                          "anyOf": [
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "uppercase"
                                }
                              },
                              "required": [
                                "type"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "lowercase"
                                }
                              },
                              "required": [
                                "type"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "trim"
                                }
                              },
                              "required": [
                                "type"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "regex"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "pattern": {
                                      "type": "string",
                                      "description": "Regular expression pattern to match"
                                    },
                                    "replacement": {
                                      "type": "string",
                                      "default": "",
                                      "description": "Replacement string for regex matches"
                                    }
                                  },
                                  "required": [
                                    "pattern"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "template"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "template": {
                                      "type": "string",
                                      "description": "Template string supporting {value} and {fieldName} placeholders"
                                    }
                                  },
                                  "required": [
                                    "template"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "split"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "delimiter": {
                                      "type": "string",
                                      "description": "String delimiter to split on"
                                    },
                                    "index": {
                                      "type": "integer",
                                      "minimum": 0,
                                      "description": "Index of the split segment to extract"
                                    }
                                  },
                                  "required": [
                                    "delimiter",
                                    "index"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "concat"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "prefix": {
                                      "type": "string",
                                      "default": "",
                                      "description": "Prefix string to prepend"
                                    },
                                    "suffix": {
                                      "type": "string",
                                      "default": "",
                                      "description": "Suffix string to append"
                                    }
                                  },
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "default"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "value": {
                                      "type": "string",
                                      "description": "Default value to use if field is empty"
                                    }
                                  },
                                  "required": [
                                    "value"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "date_format"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "inputFormat": {
                                      "type": "string",
                                      "description": "Input date format (optional, auto-detected if not provided)"
                                    },
                                    "outputFormat": {
                                      "type": "string",
                                      "description": "Output date format string"
                                    }
                                  },
                                  "required": [
                                    "outputFormat"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            }
                          ]
                        },
                        "description": "Optional chain of transforms to apply to the extracted value"
                      }
                    },
                    "required": [
                      "source"
                    ],
                    "additionalProperties": false
                  },
                  "customerEmail": {
                    "type": "object",
                    "properties": {
                      "source": {
                        "type": "string",
                        "description": "JSONPath expression to extract value from API response"
                      },
                      "transforms": {
                        "type": "array",
                        "items": {
                          "anyOf": [
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "uppercase"
                                }
                              },
                              "required": [
                                "type"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "lowercase"
                                }
                              },
                              "required": [
                                "type"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "trim"
                                }
                              },
                              "required": [
                                "type"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "regex"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "pattern": {
                                      "type": "string",
                                      "description": "Regular expression pattern to match"
                                    },
                                    "replacement": {
                                      "type": "string",
                                      "default": "",
                                      "description": "Replacement string for regex matches"
                                    }
                                  },
                                  "required": [
                                    "pattern"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "template"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "template": {
                                      "type": "string",
                                      "description": "Template string supporting {value} and {fieldName} placeholders"
                                    }
                                  },
                                  "required": [
                                    "template"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "split"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "delimiter": {
                                      "type": "string",
                                      "description": "String delimiter to split on"
                                    },
                                    "index": {
                                      "type": "integer",
                                      "minimum": 0,
                                      "description": "Index of the split segment to extract"
                                    }
                                  },
                                  "required": [
                                    "delimiter",
                                    "index"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "concat"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "prefix": {
                                      "type": "string",
                                      "default": "",
                                      "description": "Prefix string to prepend"
                                    },
                                    "suffix": {
                                      "type": "string",
                                      "default": "",
                                      "description": "Suffix string to append"
                                    }
                                  },
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "default"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "value": {
                                      "type": "string",
                                      "description": "Default value to use if field is empty"
                                    }
                                  },
                                  "required": [
                                    "value"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "date_format"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "inputFormat": {
                                      "type": "string",
                                      "description": "Input date format (optional, auto-detected if not provided)"
                                    },
                                    "outputFormat": {
                                      "type": "string",
                                      "description": "Output date format string"
                                    }
                                  },
                                  "required": [
                                    "outputFormat"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            }
                          ]
                        },
                        "description": "Optional chain of transforms to apply to the extracted value"
                      }
                    },
                    "required": [
                      "source"
                    ],
                    "additionalProperties": false
                  },
                  "customerAddressLine1": {
                    "type": "object",
                    "properties": {
                      "source": {
                        "type": "string",
                        "description": "JSONPath expression to extract value from API response"
                      },
                      "transforms": {
                        "type": "array",
                        "items": {
                          "anyOf": [
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "uppercase"
                                }
                              },
                              "required": [
                                "type"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "lowercase"
                                }
                              },
                              "required": [
                                "type"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "trim"
                                }
                              },
                              "required": [
                                "type"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "regex"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "pattern": {
                                      "type": "string",
                                      "description": "Regular expression pattern to match"
                                    },
                                    "replacement": {
                                      "type": "string",
                                      "default": "",
                                      "description": "Replacement string for regex matches"
                                    }
                                  },
                                  "required": [
                                    "pattern"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "template"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "template": {
                                      "type": "string",
                                      "description": "Template string supporting {value} and {fieldName} placeholders"
                                    }
                                  },
                                  "required": [
                                    "template"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "split"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "delimiter": {
                                      "type": "string",
                                      "description": "String delimiter to split on"
                                    },
                                    "index": {
                                      "type": "integer",
                                      "minimum": 0,
                                      "description": "Index of the split segment to extract"
                                    }
                                  },
                                  "required": [
                                    "delimiter",
                                    "index"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "concat"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "prefix": {
                                      "type": "string",
                                      "default": "",
                                      "description": "Prefix string to prepend"
                                    },
                                    "suffix": {
                                      "type": "string",
                                      "default": "",
                                      "description": "Suffix string to append"
                                    }
                                  },
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "default"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "value": {
                                      "type": "string",
                                      "description": "Default value to use if field is empty"
                                    }
                                  },
                                  "required": [
                                    "value"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "date_format"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "inputFormat": {
                                      "type": "string",
                                      "description": "Input date format (optional, auto-detected if not provided)"
                                    },
                                    "outputFormat": {
                                      "type": "string",
                                      "description": "Output date format string"
                                    }
                                  },
                                  "required": [
                                    "outputFormat"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            }
                          ]
                        },
                        "description": "Optional chain of transforms to apply to the extracted value"
                      }
                    },
                    "required": [
                      "source"
                    ],
                    "additionalProperties": false
                  },
                  "customerAddressLine2": {
                    "type": "object",
                    "properties": {
                      "source": {
                        "type": "string",
                        "description": "JSONPath expression to extract value from API response"
                      },
                      "transforms": {
                        "type": "array",
                        "items": {
                          "anyOf": [
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "uppercase"
                                }
                              },
                              "required": [
                                "type"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "lowercase"
                                }
                              },
                              "required": [
                                "type"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "trim"
                                }
                              },
                              "required": [
                                "type"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "regex"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "pattern": {
                                      "type": "string",
                                      "description": "Regular expression pattern to match"
                                    },
                                    "replacement": {
                                      "type": "string",
                                      "default": "",
                                      "description": "Replacement string for regex matches"
                                    }
                                  },
                                  "required": [
                                    "pattern"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "template"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "template": {
                                      "type": "string",
                                      "description": "Template string supporting {value} and {fieldName} placeholders"
                                    }
                                  },
                                  "required": [
                                    "template"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "split"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "delimiter": {
                                      "type": "string",
                                      "description": "String delimiter to split on"
                                    },
                                    "index": {
                                      "type": "integer",
                                      "minimum": 0,
                                      "description": "Index of the split segment to extract"
                                    }
                                  },
                                  "required": [
                                    "delimiter",
                                    "index"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "concat"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "prefix": {
                                      "type": "string",
                                      "default": "",
                                      "description": "Prefix string to prepend"
                                    },
                                    "suffix": {
                                      "type": "string",
                                      "default": "",
                                      "description": "Suffix string to append"
                                    }
                                  },
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "default"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "value": {
                                      "type": "string",
                                      "description": "Default value to use if field is empty"
                                    }
                                  },
                                  "required": [
                                    "value"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "date_format"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "inputFormat": {
                                      "type": "string",
                                      "description": "Input date format (optional, auto-detected if not provided)"
                                    },
                                    "outputFormat": {
                                      "type": "string",
                                      "description": "Output date format string"
                                    }
                                  },
                                  "required": [
                                    "outputFormat"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            }
                          ]
                        },
                        "description": "Optional chain of transforms to apply to the extracted value"
                      }
                    },
                    "required": [
                      "source"
                    ],
                    "additionalProperties": false
                  },
                  "customerAddressCity": {
                    "type": "object",
                    "properties": {
                      "source": {
                        "type": "string",
                        "description": "JSONPath expression to extract value from API response"
                      },
                      "transforms": {
                        "type": "array",
                        "items": {
                          "anyOf": [
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "uppercase"
                                }
                              },
                              "required": [
                                "type"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "lowercase"
                                }
                              },
                              "required": [
                                "type"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "trim"
                                }
                              },
                              "required": [
                                "type"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "regex"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "pattern": {
                                      "type": "string",
                                      "description": "Regular expression pattern to match"
                                    },
                                    "replacement": {
                                      "type": "string",
                                      "default": "",
                                      "description": "Replacement string for regex matches"
                                    }
                                  },
                                  "required": [
                                    "pattern"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "template"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "template": {
                                      "type": "string",
                                      "description": "Template string supporting {value} and {fieldName} placeholders"
                                    }
                                  },
                                  "required": [
                                    "template"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "split"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "delimiter": {
                                      "type": "string",
                                      "description": "String delimiter to split on"
                                    },
                                    "index": {
                                      "type": "integer",
                                      "minimum": 0,
                                      "description": "Index of the split segment to extract"
                                    }
                                  },
                                  "required": [
                                    "delimiter",
                                    "index"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "concat"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "prefix": {
                                      "type": "string",
                                      "default": "",
                                      "description": "Prefix string to prepend"
                                    },
                                    "suffix": {
                                      "type": "string",
                                      "default": "",
                                      "description": "Suffix string to append"
                                    }
                                  },
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "default"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "value": {
                                      "type": "string",
                                      "description": "Default value to use if field is empty"
                                    }
                                  },
                                  "required": [
                                    "value"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "date_format"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "inputFormat": {
                                      "type": "string",
                                      "description": "Input date format (optional, auto-detected if not provided)"
                                    },
                                    "outputFormat": {
                                      "type": "string",
                                      "description": "Output date format string"
                                    }
                                  },
                                  "required": [
                                    "outputFormat"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            }
                          ]
                        },
                        "description": "Optional chain of transforms to apply to the extracted value"
                      }
                    },
                    "required": [
                      "source"
                    ],
                    "additionalProperties": false
                  },
                  "customerAddressState": {
                    "type": "object",
                    "properties": {
                      "source": {
                        "type": "string",
                        "description": "JSONPath expression to extract value from API response"
                      },
                      "transforms": {
                        "type": "array",
                        "items": {
                          "anyOf": [
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "uppercase"
                                }
                              },
                              "required": [
                                "type"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "lowercase"
                                }
                              },
                              "required": [
                                "type"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "trim"
                                }
                              },
                              "required": [
                                "type"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "regex"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "pattern": {
                                      "type": "string",
                                      "description": "Regular expression pattern to match"
                                    },
                                    "replacement": {
                                      "type": "string",
                                      "default": "",
                                      "description": "Replacement string for regex matches"
                                    }
                                  },
                                  "required": [
                                    "pattern"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "template"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "template": {
                                      "type": "string",
                                      "description": "Template string supporting {value} and {fieldName} placeholders"
                                    }
                                  },
                                  "required": [
                                    "template"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "split"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "delimiter": {
                                      "type": "string",
                                      "description": "String delimiter to split on"
                                    },
                                    "index": {
                                      "type": "integer",
                                      "minimum": 0,
                                      "description": "Index of the split segment to extract"
                                    }
                                  },
                                  "required": [
                                    "delimiter",
                                    "index"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "concat"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "prefix": {
                                      "type": "string",
                                      "default": "",
                                      "description": "Prefix string to prepend"
                                    },
                                    "suffix": {
                                      "type": "string",
                                      "default": "",
                                      "description": "Suffix string to append"
                                    }
                                  },
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "default"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "value": {
                                      "type": "string",
                                      "description": "Default value to use if field is empty"
                                    }
                                  },
                                  "required": [
                                    "value"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "date_format"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "inputFormat": {
                                      "type": "string",
                                      "description": "Input date format (optional, auto-detected if not provided)"
                                    },
                                    "outputFormat": {
                                      "type": "string",
                                      "description": "Output date format string"
                                    }
                                  },
                                  "required": [
                                    "outputFormat"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            }
                          ]
                        },
                        "description": "Optional chain of transforms to apply to the extracted value"
                      }
                    },
                    "required": [
                      "source"
                    ],
                    "additionalProperties": false
                  },
                  "customerAddressZip": {
                    "type": "object",
                    "properties": {
                      "source": {
                        "type": "string",
                        "description": "JSONPath expression to extract value from API response"
                      },
                      "transforms": {
                        "type": "array",
                        "items": {
                          "anyOf": [
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "uppercase"
                                }
                              },
                              "required": [
                                "type"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "lowercase"
                                }
                              },
                              "required": [
                                "type"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "trim"
                                }
                              },
                              "required": [
                                "type"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "regex"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "pattern": {
                                      "type": "string",
                                      "description": "Regular expression pattern to match"
                                    },
                                    "replacement": {
                                      "type": "string",
                                      "default": "",
                                      "description": "Replacement string for regex matches"
                                    }
                                  },
                                  "required": [
                                    "pattern"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "template"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "template": {
                                      "type": "string",
                                      "description": "Template string supporting {value} and {fieldName} placeholders"
                                    }
                                  },
                                  "required": [
                                    "template"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "split"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "delimiter": {
                                      "type": "string",
                                      "description": "String delimiter to split on"
                                    },
                                    "index": {
                                      "type": "integer",
                                      "minimum": 0,
                                      "description": "Index of the split segment to extract"
                                    }
                                  },
                                  "required": [
                                    "delimiter",
                                    "index"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "concat"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "prefix": {
                                      "type": "string",
                                      "default": "",
                                      "description": "Prefix string to prepend"
                                    },
                                    "suffix": {
                                      "type": "string",
                                      "default": "",
                                      "description": "Suffix string to append"
                                    }
                                  },
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "default"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "value": {
                                      "type": "string",
                                      "description": "Default value to use if field is empty"
                                    }
                                  },
                                  "required": [
                                    "value"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "date_format"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "inputFormat": {
                                      "type": "string",
                                      "description": "Input date format (optional, auto-detected if not provided)"
                                    },
                                    "outputFormat": {
                                      "type": "string",
                                      "description": "Output date format string"
                                    }
                                  },
                                  "required": [
                                    "outputFormat"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            }
                          ]
                        },
                        "description": "Optional chain of transforms to apply to the extracted value"
                      }
                    },
                    "required": [
                      "source"
                    ],
                    "additionalProperties": false
                  },
                  "vin": {
                    "type": "object",
                    "properties": {
                      "source": {
                        "type": "string",
                        "description": "JSONPath expression to extract value from API response"
                      },
                      "transforms": {
                        "type": "array",
                        "items": {
                          "anyOf": [
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "uppercase"
                                }
                              },
                              "required": [
                                "type"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "lowercase"
                                }
                              },
                              "required": [
                                "type"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "trim"
                                }
                              },
                              "required": [
                                "type"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "regex"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "pattern": {
                                      "type": "string",
                                      "description": "Regular expression pattern to match"
                                    },
                                    "replacement": {
                                      "type": "string",
                                      "default": "",
                                      "description": "Replacement string for regex matches"
                                    }
                                  },
                                  "required": [
                                    "pattern"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "template"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "template": {
                                      "type": "string",
                                      "description": "Template string supporting {value} and {fieldName} placeholders"
                                    }
                                  },
                                  "required": [
                                    "template"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "split"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "delimiter": {
                                      "type": "string",
                                      "description": "String delimiter to split on"
                                    },
                                    "index": {
                                      "type": "integer",
                                      "minimum": 0,
                                      "description": "Index of the split segment to extract"
                                    }
                                  },
                                  "required": [
                                    "delimiter",
                                    "index"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "concat"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "prefix": {
                                      "type": "string",
                                      "default": "",
                                      "description": "Prefix string to prepend"
                                    },
                                    "suffix": {
                                      "type": "string",
                                      "default": "",
                                      "description": "Suffix string to append"
                                    }
                                  },
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "default"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "value": {
                                      "type": "string",
                                      "description": "Default value to use if field is empty"
                                    }
                                  },
                                  "required": [
                                    "value"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "date_format"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "inputFormat": {
                                      "type": "string",
                                      "description": "Input date format (optional, auto-detected if not provided)"
                                    },
                                    "outputFormat": {
                                      "type": "string",
                                      "description": "Output date format string"
                                    }
                                  },
                                  "required": [
                                    "outputFormat"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            }
                          ]
                        },
                        "description": "Optional chain of transforms to apply to the extracted value"
                      }
                    },
                    "required": [
                      "source"
                    ],
                    "additionalProperties": false
                  },
                  "year": {
                    "type": "object",
                    "properties": {
                      "source": {
                        "type": "string",
                        "description": "JSONPath expression to extract value from API response"
                      },
                      "transforms": {
                        "type": "array",
                        "items": {
                          "anyOf": [
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "uppercase"
                                }
                              },
                              "required": [
                                "type"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "lowercase"
                                }
                              },
                              "required": [
                                "type"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "trim"
                                }
                              },
                              "required": [
                                "type"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "regex"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "pattern": {
                                      "type": "string",
                                      "description": "Regular expression pattern to match"
                                    },
                                    "replacement": {
                                      "type": "string",
                                      "default": "",
                                      "description": "Replacement string for regex matches"
                                    }
                                  },
                                  "required": [
                                    "pattern"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "template"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "template": {
                                      "type": "string",
                                      "description": "Template string supporting {value} and {fieldName} placeholders"
                                    }
                                  },
                                  "required": [
                                    "template"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "split"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "delimiter": {
                                      "type": "string",
                                      "description": "String delimiter to split on"
                                    },
                                    "index": {
                                      "type": "integer",
                                      "minimum": 0,
                                      "description": "Index of the split segment to extract"
                                    }
                                  },
                                  "required": [
                                    "delimiter",
                                    "index"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "concat"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "prefix": {
                                      "type": "string",
                                      "default": "",
                                      "description": "Prefix string to prepend"
                                    },
                                    "suffix": {
                                      "type": "string",
                                      "default": "",
                                      "description": "Suffix string to append"
                                    }
                                  },
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "default"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "value": {
                                      "type": "string",
                                      "description": "Default value to use if field is empty"
                                    }
                                  },
                                  "required": [
                                    "value"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "date_format"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "inputFormat": {
                                      "type": "string",
                                      "description": "Input date format (optional, auto-detected if not provided)"
                                    },
                                    "outputFormat": {
                                      "type": "string",
                                      "description": "Output date format string"
                                    }
                                  },
                                  "required": [
                                    "outputFormat"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            }
                          ]
                        },
                        "description": "Optional chain of transforms to apply to the extracted value"
                      }
                    },
                    "required": [
                      "source"
                    ],
                    "additionalProperties": false
                  },
                  "make": {
                    "type": "object",
                    "properties": {
                      "source": {
                        "type": "string",
                        "description": "JSONPath expression to extract value from API response"
                      },
                      "transforms": {
                        "type": "array",
                        "items": {
                          "anyOf": [
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "uppercase"
                                }
                              },
                              "required": [
                                "type"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "lowercase"
                                }
                              },
                              "required": [
                                "type"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "trim"
                                }
                              },
                              "required": [
                                "type"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "regex"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "pattern": {
                                      "type": "string",
                                      "description": "Regular expression pattern to match"
                                    },
                                    "replacement": {
                                      "type": "string",
                                      "default": "",
                                      "description": "Replacement string for regex matches"
                                    }
                                  },
                                  "required": [
                                    "pattern"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "template"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "template": {
                                      "type": "string",
                                      "description": "Template string supporting {value} and {fieldName} placeholders"
                                    }
                                  },
                                  "required": [
                                    "template"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "split"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "delimiter": {
                                      "type": "string",
                                      "description": "String delimiter to split on"
                                    },
                                    "index": {
                                      "type": "integer",
                                      "minimum": 0,
                                      "description": "Index of the split segment to extract"
                                    }
                                  },
                                  "required": [
                                    "delimiter",
                                    "index"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "concat"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "prefix": {
                                      "type": "string",
                                      "default": "",
                                      "description": "Prefix string to prepend"
                                    },
                                    "suffix": {
                                      "type": "string",
                                      "default": "",
                                      "description": "Suffix string to append"
                                    }
                                  },
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "default"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "value": {
                                      "type": "string",
                                      "description": "Default value to use if field is empty"
                                    }
                                  },
                                  "required": [
                                    "value"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "date_format"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "inputFormat": {
                                      "type": "string",
                                      "description": "Input date format (optional, auto-detected if not provided)"
                                    },
                                    "outputFormat": {
                                      "type": "string",
                                      "description": "Output date format string"
                                    }
                                  },
                                  "required": [
                                    "outputFormat"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            }
                          ]
                        },
                        "description": "Optional chain of transforms to apply to the extracted value"
                      }
                    },
                    "required": [
                      "source"
                    ],
                    "additionalProperties": false
                  },
                  "model": {
                    "type": "object",
                    "properties": {
                      "source": {
                        "type": "string",
                        "description": "JSONPath expression to extract value from API response"
                      },
                      "transforms": {
                        "type": "array",
                        "items": {
                          "anyOf": [
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "uppercase"
                                }
                              },
                              "required": [
                                "type"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "lowercase"
                                }
                              },
                              "required": [
                                "type"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "trim"
                                }
                              },
                              "required": [
                                "type"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "regex"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "pattern": {
                                      "type": "string",
                                      "description": "Regular expression pattern to match"
                                    },
                                    "replacement": {
                                      "type": "string",
                                      "default": "",
                                      "description": "Replacement string for regex matches"
                                    }
                                  },
                                  "required": [
                                    "pattern"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "template"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "template": {
                                      "type": "string",
                                      "description": "Template string supporting {value} and {fieldName} placeholders"
                                    }
                                  },
                                  "required": [
                                    "template"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "split"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "delimiter": {
                                      "type": "string",
                                      "description": "String delimiter to split on"
                                    },
                                    "index": {
                                      "type": "integer",
                                      "minimum": 0,
                                      "description": "Index of the split segment to extract"
                                    }
                                  },
                                  "required": [
                                    "delimiter",
                                    "index"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "concat"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "prefix": {
                                      "type": "string",
                                      "default": "",
                                      "description": "Prefix string to prepend"
                                    },
                                    "suffix": {
                                      "type": "string",
                                      "default": "",
                                      "description": "Suffix string to append"
                                    }
                                  },
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "default"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "value": {
                                      "type": "string",
                                      "description": "Default value to use if field is empty"
                                    }
                                  },
                                  "required": [
                                    "value"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "date_format"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "inputFormat": {
                                      "type": "string",
                                      "description": "Input date format (optional, auto-detected if not provided)"
                                    },
                                    "outputFormat": {
                                      "type": "string",
                                      "description": "Output date format string"
                                    }
                                  },
                                  "required": [
                                    "outputFormat"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            }
                          ]
                        },
                        "description": "Optional chain of transforms to apply to the extracted value"
                      }
                    },
                    "required": [
                      "source"
                    ],
                    "additionalProperties": false
                  },
                  "color": {
                    "type": "object",
                    "properties": {
                      "source": {
                        "type": "string",
                        "description": "JSONPath expression to extract value from API response"
                      },
                      "transforms": {
                        "type": "array",
                        "items": {
                          "anyOf": [
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "uppercase"
                                }
                              },
                              "required": [
                                "type"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "lowercase"
                                }
                              },
                              "required": [
                                "type"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "trim"
                                }
                              },
                              "required": [
                                "type"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "regex"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "pattern": {
                                      "type": "string",
                                      "description": "Regular expression pattern to match"
                                    },
                                    "replacement": {
                                      "type": "string",
                                      "default": "",
                                      "description": "Replacement string for regex matches"
                                    }
                                  },
                                  "required": [
                                    "pattern"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "template"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "template": {
                                      "type": "string",
                                      "description": "Template string supporting {value} and {fieldName} placeholders"
                                    }
                                  },
                                  "required": [
                                    "template"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "split"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "delimiter": {
                                      "type": "string",
                                      "description": "String delimiter to split on"
                                    },
                                    "index": {
                                      "type": "integer",
                                      "minimum": 0,
                                      "description": "Index of the split segment to extract"
                                    }
                                  },
                                  "required": [
                                    "delimiter",
                                    "index"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "concat"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "prefix": {
                                      "type": "string",
                                      "default": "",
                                      "description": "Prefix string to prepend"
                                    },
                                    "suffix": {
                                      "type": "string",
                                      "default": "",
                                      "description": "Suffix string to append"
                                    }
                                  },
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "default"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "value": {
                                      "type": "string",
                                      "description": "Default value to use if field is empty"
                                    }
                                  },
                                  "required": [
                                    "value"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "date_format"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "inputFormat": {
                                      "type": "string",
                                      "description": "Input date format (optional, auto-detected if not provided)"
                                    },
                                    "outputFormat": {
                                      "type": "string",
                                      "description": "Output date format string"
                                    }
                                  },
                                  "required": [
                                    "outputFormat"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            }
                          ]
                        },
                        "description": "Optional chain of transforms to apply to the extracted value"
                      }
                    },
                    "required": [
                      "source"
                    ],
                    "additionalProperties": false
                  },
                  "stockNumber": {
                    "type": "object",
                    "properties": {
                      "source": {
                        "type": "string",
                        "description": "JSONPath expression to extract value from API response"
                      },
                      "transforms": {
                        "type": "array",
                        "items": {
                          "anyOf": [
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "uppercase"
                                }
                              },
                              "required": [
                                "type"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "lowercase"
                                }
                              },
                              "required": [
                                "type"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "trim"
                                }
                              },
                              "required": [
                                "type"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "regex"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "pattern": {
                                      "type": "string",
                                      "description": "Regular expression pattern to match"
                                    },
                                    "replacement": {
                                      "type": "string",
                                      "default": "",
                                      "description": "Replacement string for regex matches"
                                    }
                                  },
                                  "required": [
                                    "pattern"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "template"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "template": {
                                      "type": "string",
                                      "description": "Template string supporting {value} and {fieldName} placeholders"
                                    }
                                  },
                                  "required": [
                                    "template"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "split"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "delimiter": {
                                      "type": "string",
                                      "description": "String delimiter to split on"
                                    },
                                    "index": {
                                      "type": "integer",
                                      "minimum": 0,
                                      "description": "Index of the split segment to extract"
                                    }
                                  },
                                  "required": [
                                    "delimiter",
                                    "index"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "concat"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "prefix": {
                                      "type": "string",
                                      "default": "",
                                      "description": "Prefix string to prepend"
                                    },
                                    "suffix": {
                                      "type": "string",
                                      "default": "",
                                      "description": "Suffix string to append"
                                    }
                                  },
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "default"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "value": {
                                      "type": "string",
                                      "description": "Default value to use if field is empty"
                                    }
                                  },
                                  "required": [
                                    "value"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "date_format"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "inputFormat": {
                                      "type": "string",
                                      "description": "Input date format (optional, auto-detected if not provided)"
                                    },
                                    "outputFormat": {
                                      "type": "string",
                                      "description": "Output date format string"
                                    }
                                  },
                                  "required": [
                                    "outputFormat"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            }
                          ]
                        },
                        "description": "Optional chain of transforms to apply to the extracted value"
                      }
                    },
                    "required": [
                      "source"
                    ],
                    "additionalProperties": false
                  },
                  "licensePlate": {
                    "type": "object",
                    "properties": {
                      "source": {
                        "type": "string",
                        "description": "JSONPath expression to extract value from API response"
                      },
                      "transforms": {
                        "type": "array",
                        "items": {
                          "anyOf": [
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "uppercase"
                                }
                              },
                              "required": [
                                "type"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "lowercase"
                                }
                              },
                              "required": [
                                "type"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "trim"
                                }
                              },
                              "required": [
                                "type"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "regex"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "pattern": {
                                      "type": "string",
                                      "description": "Regular expression pattern to match"
                                    },
                                    "replacement": {
                                      "type": "string",
                                      "default": "",
                                      "description": "Replacement string for regex matches"
                                    }
                                  },
                                  "required": [
                                    "pattern"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "template"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "template": {
                                      "type": "string",
                                      "description": "Template string supporting {value} and {fieldName} placeholders"
                                    }
                                  },
                                  "required": [
                                    "template"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "split"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "delimiter": {
                                      "type": "string",
                                      "description": "String delimiter to split on"
                                    },
                                    "index": {
                                      "type": "integer",
                                      "minimum": 0,
                                      "description": "Index of the split segment to extract"
                                    }
                                  },
                                  "required": [
                                    "delimiter",
                                    "index"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "concat"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "prefix": {
                                      "type": "string",
                                      "default": "",
                                      "description": "Prefix string to prepend"
                                    },
                                    "suffix": {
                                      "type": "string",
                                      "default": "",
                                      "description": "Suffix string to append"
                                    }
                                  },
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "default"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "value": {
                                      "type": "string",
                                      "description": "Default value to use if field is empty"
                                    }
                                  },
                                  "required": [
                                    "value"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            },
                            {
                              "type": "object",
                              "properties": {
                                "type": {
                                  "type": "string",
                                  "const": "date_format"
                                },
                                "config": {
                                  "type": "object",
                                  "properties": {
                                    "inputFormat": {
                                      "type": "string",
                                      "description": "Input date format (optional, auto-detected if not provided)"
                                    },
                                    "outputFormat": {
                                      "type": "string",
                                      "description": "Output date format string"
                                    }
                                  },
                                  "required": [
                                    "outputFormat"
                                  ],
                                  "additionalProperties": false
                                }
                              },
                              "required": [
                                "type",
                                "config"
                              ],
                              "additionalProperties": false
                            }
                          ]
                        },
                        "description": "Optional chain of transforms to apply to the extracted value"
                      }
                    },
                    "required": [
                      "source"
                    ],
                    "additionalProperties": false
                  }
                },
                "additionalProperties": false,
                "description": "Field mappings from 3rd party API response to canonical FetchResult"
              },
              "enrichments": {
                "type": "array",
                "items": {
                  "type": "object",
                  "properties": {
                    "condition": {
                      "type": "string",
                      "description": "JSONPath condition evaluated against the **raw primary API response**.\n\nEnrichment is skipped if the condition returns a falsy value (`null`, `undefined`, `\"\"`, `0`, `false`, or empty array).\n\n**Condition Styles:**\n\n| Style | Syntax | Behavior |\n|-------|--------|----------|\n| Simple existence | `$.id` | Truthy if field exists and has value |\n| Filter expression | `$[?(@.field == \"value\")]` | Truthy if filter matches |\n\n**Filter Expression Examples:**\n\n| Condition | Description |\n|-----------|-------------|\n| `$[?(@.tier == \"premium\")]` | Tier equals \"premium\" |\n| `$.vehicle[?(@.year >= 2020)]` | Vehicle year is 2020 or newer |\n| `$.features[?(@ == \"roadside_assistance\")]` | Features array contains value |\n| `$.customer[?(@.membership_level == \"gold\")]` | Nested property equals value |\n| `$.vehicles[?(@.primary == true)]` | Array contains object with primary=true |"
                    },
                    "authProvider": {
                      "type": "string",
                      "description": "Auth provider to use for this enrichment request.\n\nDefaults to the parent fetch trigger's `authProvider` if not specified. Use this to authenticate enrichment calls against a different API than the primary request."
                    },
                    "httpMethod": {
                      "type": "string",
                      "enum": [
                        "GET",
                        "POST",
                        "PUT",
                        "PATCH"
                      ],
                      "default": "GET",
                      "description": "HTTP method for the enrichment API call.\n\nDefaults to `GET`. Use `POST`, `PUT`, or `PATCH` when the enrichment endpoint requires a request body."
                    },
                    "endpoint": {
                      "type": "string",
                      "description": "Endpoint URL with variable interpolation.\n\n**Supported Variables:**\n\n| Variable | Source | Example |\n|----------|--------|---------|\n| `{primary.fieldName}` | Mapped field from primary response | `{primary.customerId}` |\n| `{primary_raw.$.path}` | JSONPath from raw primary response | `{primary_raw.$.data[0].id}` |\n| `{lookup_value}` | Original lookup value | `{lookup_value}` |\n\n**Example:**\n```\nhttps://api.example.com/customers/{primary.customerId}/address\n```\n\nAll interpolated values are URL-encoded automatically."
                    },
                    "requestBody": {
                      "type": "string",
                      "description": "Request body template for `POST`, `PUT`, or `PATCH` requests.\n\nSupports the same variable interpolation as `endpoint`:\n- `{primary.fieldName}` - Mapped field from primary response\n- `{primary_raw.$.path}` - JSONPath from raw primary response\n- `{lookup_value}` - Original lookup value\n\n**Example:**\n```json\n{\"customerId\": \"{primary.customerId}\", \"action\": \"getAddress\"}\n```"
                    },
                    "headers": {
                      "type": "object",
                      "additionalProperties": {
                        "type": "string"
                      },
                      "description": "Optional custom headers to include with the enrichment request.\n\nSupports the same variable interpolation as `endpoint`:\n- `{primary.fieldName}` - Mapped field from primary response\n- `{primary_raw.$.path}` - JSONPath from raw primary response\n- `{lookup_value}` - Original lookup value\n\n**Example:**\n```json\n{ \"api-version\": \"4\", \"x-tenant-id\": \"{primary.tenantId}\" }\n```",
                      "default": {}
                    },
                    "mappings": {
                      "type": "object",
                      "properties": {
                        "id": {
                          "type": "object",
                          "properties": {
                            "source": {
                              "type": "string",
                              "description": "JSONPath expression to extract value from API response"
                            },
                            "transforms": {
                              "type": "array",
                              "items": {
                                "anyOf": [
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "uppercase"
                                      }
                                    },
                                    "required": [
                                      "type"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "lowercase"
                                      }
                                    },
                                    "required": [
                                      "type"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "trim"
                                      }
                                    },
                                    "required": [
                                      "type"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "regex"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "pattern": {
                                            "type": "string",
                                            "description": "Regular expression pattern to match"
                                          },
                                          "replacement": {
                                            "type": "string",
                                            "default": "",
                                            "description": "Replacement string for regex matches"
                                          }
                                        },
                                        "required": [
                                          "pattern"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "template"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "template": {
                                            "type": "string",
                                            "description": "Template string supporting {value} and {fieldName} placeholders"
                                          }
                                        },
                                        "required": [
                                          "template"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "split"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "delimiter": {
                                            "type": "string",
                                            "description": "String delimiter to split on"
                                          },
                                          "index": {
                                            "type": "integer",
                                            "minimum": 0,
                                            "description": "Index of the split segment to extract"
                                          }
                                        },
                                        "required": [
                                          "delimiter",
                                          "index"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "concat"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "prefix": {
                                            "type": "string",
                                            "default": "",
                                            "description": "Prefix string to prepend"
                                          },
                                          "suffix": {
                                            "type": "string",
                                            "default": "",
                                            "description": "Suffix string to append"
                                          }
                                        },
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "default"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "value": {
                                            "type": "string",
                                            "description": "Default value to use if field is empty"
                                          }
                                        },
                                        "required": [
                                          "value"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "date_format"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "inputFormat": {
                                            "type": "string",
                                            "description": "Input date format (optional, auto-detected if not provided)"
                                          },
                                          "outputFormat": {
                                            "type": "string",
                                            "description": "Output date format string"
                                          }
                                        },
                                        "required": [
                                          "outputFormat"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  }
                                ]
                              },
                              "description": "Optional chain of transforms to apply to the extracted value"
                            }
                          },
                          "required": [
                            "source"
                          ],
                          "additionalProperties": false
                        },
                        "displayLabel": {
                          "type": "object",
                          "properties": {
                            "source": {
                              "type": "string",
                              "description": "JSONPath expression to extract value from API response"
                            },
                            "transforms": {
                              "type": "array",
                              "items": {
                                "anyOf": [
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "uppercase"
                                      }
                                    },
                                    "required": [
                                      "type"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "lowercase"
                                      }
                                    },
                                    "required": [
                                      "type"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "trim"
                                      }
                                    },
                                    "required": [
                                      "type"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "regex"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "pattern": {
                                            "type": "string",
                                            "description": "Regular expression pattern to match"
                                          },
                                          "replacement": {
                                            "type": "string",
                                            "default": "",
                                            "description": "Replacement string for regex matches"
                                          }
                                        },
                                        "required": [
                                          "pattern"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "template"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "template": {
                                            "type": "string",
                                            "description": "Template string supporting {value} and {fieldName} placeholders"
                                          }
                                        },
                                        "required": [
                                          "template"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "split"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "delimiter": {
                                            "type": "string",
                                            "description": "String delimiter to split on"
                                          },
                                          "index": {
                                            "type": "integer",
                                            "minimum": 0,
                                            "description": "Index of the split segment to extract"
                                          }
                                        },
                                        "required": [
                                          "delimiter",
                                          "index"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "concat"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "prefix": {
                                            "type": "string",
                                            "default": "",
                                            "description": "Prefix string to prepend"
                                          },
                                          "suffix": {
                                            "type": "string",
                                            "default": "",
                                            "description": "Suffix string to append"
                                          }
                                        },
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "default"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "value": {
                                            "type": "string",
                                            "description": "Default value to use if field is empty"
                                          }
                                        },
                                        "required": [
                                          "value"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "date_format"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "inputFormat": {
                                            "type": "string",
                                            "description": "Input date format (optional, auto-detected if not provided)"
                                          },
                                          "outputFormat": {
                                            "type": "string",
                                            "description": "Output date format string"
                                          }
                                        },
                                        "required": [
                                          "outputFormat"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  }
                                ]
                              },
                              "description": "Optional chain of transforms to apply to the extracted value"
                            }
                          },
                          "required": [
                            "source"
                          ],
                          "additionalProperties": false
                        },
                        "referenceNumber": {
                          "type": "object",
                          "properties": {
                            "source": {
                              "type": "string",
                              "description": "JSONPath expression to extract value from API response"
                            },
                            "transforms": {
                              "type": "array",
                              "items": {
                                "anyOf": [
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "uppercase"
                                      }
                                    },
                                    "required": [
                                      "type"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "lowercase"
                                      }
                                    },
                                    "required": [
                                      "type"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "trim"
                                      }
                                    },
                                    "required": [
                                      "type"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "regex"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "pattern": {
                                            "type": "string",
                                            "description": "Regular expression pattern to match"
                                          },
                                          "replacement": {
                                            "type": "string",
                                            "default": "",
                                            "description": "Replacement string for regex matches"
                                          }
                                        },
                                        "required": [
                                          "pattern"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "template"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "template": {
                                            "type": "string",
                                            "description": "Template string supporting {value} and {fieldName} placeholders"
                                          }
                                        },
                                        "required": [
                                          "template"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "split"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "delimiter": {
                                            "type": "string",
                                            "description": "String delimiter to split on"
                                          },
                                          "index": {
                                            "type": "integer",
                                            "minimum": 0,
                                            "description": "Index of the split segment to extract"
                                          }
                                        },
                                        "required": [
                                          "delimiter",
                                          "index"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "concat"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "prefix": {
                                            "type": "string",
                                            "default": "",
                                            "description": "Prefix string to prepend"
                                          },
                                          "suffix": {
                                            "type": "string",
                                            "default": "",
                                            "description": "Suffix string to append"
                                          }
                                        },
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "default"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "value": {
                                            "type": "string",
                                            "description": "Default value to use if field is empty"
                                          }
                                        },
                                        "required": [
                                          "value"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "date_format"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "inputFormat": {
                                            "type": "string",
                                            "description": "Input date format (optional, auto-detected if not provided)"
                                          },
                                          "outputFormat": {
                                            "type": "string",
                                            "description": "Output date format string"
                                          }
                                        },
                                        "required": [
                                          "outputFormat"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  }
                                ]
                              },
                              "description": "Optional chain of transforms to apply to the extracted value"
                            }
                          },
                          "required": [
                            "source"
                          ],
                          "additionalProperties": false
                        },
                        "customerName": {
                          "type": "object",
                          "properties": {
                            "source": {
                              "type": "string",
                              "description": "JSONPath expression to extract value from API response"
                            },
                            "transforms": {
                              "type": "array",
                              "items": {
                                "anyOf": [
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "uppercase"
                                      }
                                    },
                                    "required": [
                                      "type"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "lowercase"
                                      }
                                    },
                                    "required": [
                                      "type"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "trim"
                                      }
                                    },
                                    "required": [
                                      "type"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "regex"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "pattern": {
                                            "type": "string",
                                            "description": "Regular expression pattern to match"
                                          },
                                          "replacement": {
                                            "type": "string",
                                            "default": "",
                                            "description": "Replacement string for regex matches"
                                          }
                                        },
                                        "required": [
                                          "pattern"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "template"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "template": {
                                            "type": "string",
                                            "description": "Template string supporting {value} and {fieldName} placeholders"
                                          }
                                        },
                                        "required": [
                                          "template"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "split"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "delimiter": {
                                            "type": "string",
                                            "description": "String delimiter to split on"
                                          },
                                          "index": {
                                            "type": "integer",
                                            "minimum": 0,
                                            "description": "Index of the split segment to extract"
                                          }
                                        },
                                        "required": [
                                          "delimiter",
                                          "index"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "concat"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "prefix": {
                                            "type": "string",
                                            "default": "",
                                            "description": "Prefix string to prepend"
                                          },
                                          "suffix": {
                                            "type": "string",
                                            "default": "",
                                            "description": "Suffix string to append"
                                          }
                                        },
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "default"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "value": {
                                            "type": "string",
                                            "description": "Default value to use if field is empty"
                                          }
                                        },
                                        "required": [
                                          "value"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "date_format"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "inputFormat": {
                                            "type": "string",
                                            "description": "Input date format (optional, auto-detected if not provided)"
                                          },
                                          "outputFormat": {
                                            "type": "string",
                                            "description": "Output date format string"
                                          }
                                        },
                                        "required": [
                                          "outputFormat"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  }
                                ]
                              },
                              "description": "Optional chain of transforms to apply to the extracted value"
                            }
                          },
                          "required": [
                            "source"
                          ],
                          "additionalProperties": false
                        },
                        "customerPhone": {
                          "type": "object",
                          "properties": {
                            "source": {
                              "type": "string",
                              "description": "JSONPath expression to extract value from API response"
                            },
                            "transforms": {
                              "type": "array",
                              "items": {
                                "anyOf": [
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "uppercase"
                                      }
                                    },
                                    "required": [
                                      "type"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "lowercase"
                                      }
                                    },
                                    "required": [
                                      "type"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "trim"
                                      }
                                    },
                                    "required": [
                                      "type"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "regex"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "pattern": {
                                            "type": "string",
                                            "description": "Regular expression pattern to match"
                                          },
                                          "replacement": {
                                            "type": "string",
                                            "default": "",
                                            "description": "Replacement string for regex matches"
                                          }
                                        },
                                        "required": [
                                          "pattern"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "template"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "template": {
                                            "type": "string",
                                            "description": "Template string supporting {value} and {fieldName} placeholders"
                                          }
                                        },
                                        "required": [
                                          "template"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "split"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "delimiter": {
                                            "type": "string",
                                            "description": "String delimiter to split on"
                                          },
                                          "index": {
                                            "type": "integer",
                                            "minimum": 0,
                                            "description": "Index of the split segment to extract"
                                          }
                                        },
                                        "required": [
                                          "delimiter",
                                          "index"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "concat"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "prefix": {
                                            "type": "string",
                                            "default": "",
                                            "description": "Prefix string to prepend"
                                          },
                                          "suffix": {
                                            "type": "string",
                                            "default": "",
                                            "description": "Suffix string to append"
                                          }
                                        },
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "default"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "value": {
                                            "type": "string",
                                            "description": "Default value to use if field is empty"
                                          }
                                        },
                                        "required": [
                                          "value"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "date_format"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "inputFormat": {
                                            "type": "string",
                                            "description": "Input date format (optional, auto-detected if not provided)"
                                          },
                                          "outputFormat": {
                                            "type": "string",
                                            "description": "Output date format string"
                                          }
                                        },
                                        "required": [
                                          "outputFormat"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  }
                                ]
                              },
                              "description": "Optional chain of transforms to apply to the extracted value"
                            }
                          },
                          "required": [
                            "source"
                          ],
                          "additionalProperties": false
                        },
                        "customerEmail": {
                          "type": "object",
                          "properties": {
                            "source": {
                              "type": "string",
                              "description": "JSONPath expression to extract value from API response"
                            },
                            "transforms": {
                              "type": "array",
                              "items": {
                                "anyOf": [
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "uppercase"
                                      }
                                    },
                                    "required": [
                                      "type"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "lowercase"
                                      }
                                    },
                                    "required": [
                                      "type"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "trim"
                                      }
                                    },
                                    "required": [
                                      "type"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "regex"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "pattern": {
                                            "type": "string",
                                            "description": "Regular expression pattern to match"
                                          },
                                          "replacement": {
                                            "type": "string",
                                            "default": "",
                                            "description": "Replacement string for regex matches"
                                          }
                                        },
                                        "required": [
                                          "pattern"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "template"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "template": {
                                            "type": "string",
                                            "description": "Template string supporting {value} and {fieldName} placeholders"
                                          }
                                        },
                                        "required": [
                                          "template"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "split"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "delimiter": {
                                            "type": "string",
                                            "description": "String delimiter to split on"
                                          },
                                          "index": {
                                            "type": "integer",
                                            "minimum": 0,
                                            "description": "Index of the split segment to extract"
                                          }
                                        },
                                        "required": [
                                          "delimiter",
                                          "index"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "concat"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "prefix": {
                                            "type": "string",
                                            "default": "",
                                            "description": "Prefix string to prepend"
                                          },
                                          "suffix": {
                                            "type": "string",
                                            "default": "",
                                            "description": "Suffix string to append"
                                          }
                                        },
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "default"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "value": {
                                            "type": "string",
                                            "description": "Default value to use if field is empty"
                                          }
                                        },
                                        "required": [
                                          "value"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "date_format"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "inputFormat": {
                                            "type": "string",
                                            "description": "Input date format (optional, auto-detected if not provided)"
                                          },
                                          "outputFormat": {
                                            "type": "string",
                                            "description": "Output date format string"
                                          }
                                        },
                                        "required": [
                                          "outputFormat"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  }
                                ]
                              },
                              "description": "Optional chain of transforms to apply to the extracted value"
                            }
                          },
                          "required": [
                            "source"
                          ],
                          "additionalProperties": false
                        },
                        "customerAddressLine1": {
                          "type": "object",
                          "properties": {
                            "source": {
                              "type": "string",
                              "description": "JSONPath expression to extract value from API response"
                            },
                            "transforms": {
                              "type": "array",
                              "items": {
                                "anyOf": [
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "uppercase"
                                      }
                                    },
                                    "required": [
                                      "type"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "lowercase"
                                      }
                                    },
                                    "required": [
                                      "type"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "trim"
                                      }
                                    },
                                    "required": [
                                      "type"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "regex"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "pattern": {
                                            "type": "string",
                                            "description": "Regular expression pattern to match"
                                          },
                                          "replacement": {
                                            "type": "string",
                                            "default": "",
                                            "description": "Replacement string for regex matches"
                                          }
                                        },
                                        "required": [
                                          "pattern"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "template"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "template": {
                                            "type": "string",
                                            "description": "Template string supporting {value} and {fieldName} placeholders"
                                          }
                                        },
                                        "required": [
                                          "template"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "split"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "delimiter": {
                                            "type": "string",
                                            "description": "String delimiter to split on"
                                          },
                                          "index": {
                                            "type": "integer",
                                            "minimum": 0,
                                            "description": "Index of the split segment to extract"
                                          }
                                        },
                                        "required": [
                                          "delimiter",
                                          "index"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "concat"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "prefix": {
                                            "type": "string",
                                            "default": "",
                                            "description": "Prefix string to prepend"
                                          },
                                          "suffix": {
                                            "type": "string",
                                            "default": "",
                                            "description": "Suffix string to append"
                                          }
                                        },
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "default"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "value": {
                                            "type": "string",
                                            "description": "Default value to use if field is empty"
                                          }
                                        },
                                        "required": [
                                          "value"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "date_format"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "inputFormat": {
                                            "type": "string",
                                            "description": "Input date format (optional, auto-detected if not provided)"
                                          },
                                          "outputFormat": {
                                            "type": "string",
                                            "description": "Output date format string"
                                          }
                                        },
                                        "required": [
                                          "outputFormat"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  }
                                ]
                              },
                              "description": "Optional chain of transforms to apply to the extracted value"
                            }
                          },
                          "required": [
                            "source"
                          ],
                          "additionalProperties": false
                        },
                        "customerAddressLine2": {
                          "type": "object",
                          "properties": {
                            "source": {
                              "type": "string",
                              "description": "JSONPath expression to extract value from API response"
                            },
                            "transforms": {
                              "type": "array",
                              "items": {
                                "anyOf": [
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "uppercase"
                                      }
                                    },
                                    "required": [
                                      "type"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "lowercase"
                                      }
                                    },
                                    "required": [
                                      "type"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "trim"
                                      }
                                    },
                                    "required": [
                                      "type"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "regex"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "pattern": {
                                            "type": "string",
                                            "description": "Regular expression pattern to match"
                                          },
                                          "replacement": {
                                            "type": "string",
                                            "default": "",
                                            "description": "Replacement string for regex matches"
                                          }
                                        },
                                        "required": [
                                          "pattern"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "template"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "template": {
                                            "type": "string",
                                            "description": "Template string supporting {value} and {fieldName} placeholders"
                                          }
                                        },
                                        "required": [
                                          "template"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "split"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "delimiter": {
                                            "type": "string",
                                            "description": "String delimiter to split on"
                                          },
                                          "index": {
                                            "type": "integer",
                                            "minimum": 0,
                                            "description": "Index of the split segment to extract"
                                          }
                                        },
                                        "required": [
                                          "delimiter",
                                          "index"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "concat"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "prefix": {
                                            "type": "string",
                                            "default": "",
                                            "description": "Prefix string to prepend"
                                          },
                                          "suffix": {
                                            "type": "string",
                                            "default": "",
                                            "description": "Suffix string to append"
                                          }
                                        },
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "default"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "value": {
                                            "type": "string",
                                            "description": "Default value to use if field is empty"
                                          }
                                        },
                                        "required": [
                                          "value"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "date_format"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "inputFormat": {
                                            "type": "string",
                                            "description": "Input date format (optional, auto-detected if not provided)"
                                          },
                                          "outputFormat": {
                                            "type": "string",
                                            "description": "Output date format string"
                                          }
                                        },
                                        "required": [
                                          "outputFormat"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  }
                                ]
                              },
                              "description": "Optional chain of transforms to apply to the extracted value"
                            }
                          },
                          "required": [
                            "source"
                          ],
                          "additionalProperties": false
                        },
                        "customerAddressCity": {
                          "type": "object",
                          "properties": {
                            "source": {
                              "type": "string",
                              "description": "JSONPath expression to extract value from API response"
                            },
                            "transforms": {
                              "type": "array",
                              "items": {
                                "anyOf": [
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "uppercase"
                                      }
                                    },
                                    "required": [
                                      "type"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "lowercase"
                                      }
                                    },
                                    "required": [
                                      "type"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "trim"
                                      }
                                    },
                                    "required": [
                                      "type"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "regex"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "pattern": {
                                            "type": "string",
                                            "description": "Regular expression pattern to match"
                                          },
                                          "replacement": {
                                            "type": "string",
                                            "default": "",
                                            "description": "Replacement string for regex matches"
                                          }
                                        },
                                        "required": [
                                          "pattern"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "template"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "template": {
                                            "type": "string",
                                            "description": "Template string supporting {value} and {fieldName} placeholders"
                                          }
                                        },
                                        "required": [
                                          "template"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "split"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "delimiter": {
                                            "type": "string",
                                            "description": "String delimiter to split on"
                                          },
                                          "index": {
                                            "type": "integer",
                                            "minimum": 0,
                                            "description": "Index of the split segment to extract"
                                          }
                                        },
                                        "required": [
                                          "delimiter",
                                          "index"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "concat"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "prefix": {
                                            "type": "string",
                                            "default": "",
                                            "description": "Prefix string to prepend"
                                          },
                                          "suffix": {
                                            "type": "string",
                                            "default": "",
                                            "description": "Suffix string to append"
                                          }
                                        },
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "default"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "value": {
                                            "type": "string",
                                            "description": "Default value to use if field is empty"
                                          }
                                        },
                                        "required": [
                                          "value"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "date_format"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "inputFormat": {
                                            "type": "string",
                                            "description": "Input date format (optional, auto-detected if not provided)"
                                          },
                                          "outputFormat": {
                                            "type": "string",
                                            "description": "Output date format string"
                                          }
                                        },
                                        "required": [
                                          "outputFormat"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  }
                                ]
                              },
                              "description": "Optional chain of transforms to apply to the extracted value"
                            }
                          },
                          "required": [
                            "source"
                          ],
                          "additionalProperties": false
                        },
                        "customerAddressState": {
                          "type": "object",
                          "properties": {
                            "source": {
                              "type": "string",
                              "description": "JSONPath expression to extract value from API response"
                            },
                            "transforms": {
                              "type": "array",
                              "items": {
                                "anyOf": [
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "uppercase"
                                      }
                                    },
                                    "required": [
                                      "type"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "lowercase"
                                      }
                                    },
                                    "required": [
                                      "type"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "trim"
                                      }
                                    },
                                    "required": [
                                      "type"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "regex"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "pattern": {
                                            "type": "string",
                                            "description": "Regular expression pattern to match"
                                          },
                                          "replacement": {
                                            "type": "string",
                                            "default": "",
                                            "description": "Replacement string for regex matches"
                                          }
                                        },
                                        "required": [
                                          "pattern"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "template"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "template": {
                                            "type": "string",
                                            "description": "Template string supporting {value} and {fieldName} placeholders"
                                          }
                                        },
                                        "required": [
                                          "template"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "split"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "delimiter": {
                                            "type": "string",
                                            "description": "String delimiter to split on"
                                          },
                                          "index": {
                                            "type": "integer",
                                            "minimum": 0,
                                            "description": "Index of the split segment to extract"
                                          }
                                        },
                                        "required": [
                                          "delimiter",
                                          "index"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "concat"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "prefix": {
                                            "type": "string",
                                            "default": "",
                                            "description": "Prefix string to prepend"
                                          },
                                          "suffix": {
                                            "type": "string",
                                            "default": "",
                                            "description": "Suffix string to append"
                                          }
                                        },
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "default"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "value": {
                                            "type": "string",
                                            "description": "Default value to use if field is empty"
                                          }
                                        },
                                        "required": [
                                          "value"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "date_format"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "inputFormat": {
                                            "type": "string",
                                            "description": "Input date format (optional, auto-detected if not provided)"
                                          },
                                          "outputFormat": {
                                            "type": "string",
                                            "description": "Output date format string"
                                          }
                                        },
                                        "required": [
                                          "outputFormat"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  }
                                ]
                              },
                              "description": "Optional chain of transforms to apply to the extracted value"
                            }
                          },
                          "required": [
                            "source"
                          ],
                          "additionalProperties": false
                        },
                        "customerAddressZip": {
                          "type": "object",
                          "properties": {
                            "source": {
                              "type": "string",
                              "description": "JSONPath expression to extract value from API response"
                            },
                            "transforms": {
                              "type": "array",
                              "items": {
                                "anyOf": [
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "uppercase"
                                      }
                                    },
                                    "required": [
                                      "type"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "lowercase"
                                      }
                                    },
                                    "required": [
                                      "type"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "trim"
                                      }
                                    },
                                    "required": [
                                      "type"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "regex"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "pattern": {
                                            "type": "string",
                                            "description": "Regular expression pattern to match"
                                          },
                                          "replacement": {
                                            "type": "string",
                                            "default": "",
                                            "description": "Replacement string for regex matches"
                                          }
                                        },
                                        "required": [
                                          "pattern"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "template"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "template": {
                                            "type": "string",
                                            "description": "Template string supporting {value} and {fieldName} placeholders"
                                          }
                                        },
                                        "required": [
                                          "template"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "split"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "delimiter": {
                                            "type": "string",
                                            "description": "String delimiter to split on"
                                          },
                                          "index": {
                                            "type": "integer",
                                            "minimum": 0,
                                            "description": "Index of the split segment to extract"
                                          }
                                        },
                                        "required": [
                                          "delimiter",
                                          "index"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "concat"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "prefix": {
                                            "type": "string",
                                            "default": "",
                                            "description": "Prefix string to prepend"
                                          },
                                          "suffix": {
                                            "type": "string",
                                            "default": "",
                                            "description": "Suffix string to append"
                                          }
                                        },
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "default"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "value": {
                                            "type": "string",
                                            "description": "Default value to use if field is empty"
                                          }
                                        },
                                        "required": [
                                          "value"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "date_format"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "inputFormat": {
                                            "type": "string",
                                            "description": "Input date format (optional, auto-detected if not provided)"
                                          },
                                          "outputFormat": {
                                            "type": "string",
                                            "description": "Output date format string"
                                          }
                                        },
                                        "required": [
                                          "outputFormat"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  }
                                ]
                              },
                              "description": "Optional chain of transforms to apply to the extracted value"
                            }
                          },
                          "required": [
                            "source"
                          ],
                          "additionalProperties": false
                        },
                        "vin": {
                          "type": "object",
                          "properties": {
                            "source": {
                              "type": "string",
                              "description": "JSONPath expression to extract value from API response"
                            },
                            "transforms": {
                              "type": "array",
                              "items": {
                                "anyOf": [
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "uppercase"
                                      }
                                    },
                                    "required": [
                                      "type"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "lowercase"
                                      }
                                    },
                                    "required": [
                                      "type"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "trim"
                                      }
                                    },
                                    "required": [
                                      "type"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "regex"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "pattern": {
                                            "type": "string",
                                            "description": "Regular expression pattern to match"
                                          },
                                          "replacement": {
                                            "type": "string",
                                            "default": "",
                                            "description": "Replacement string for regex matches"
                                          }
                                        },
                                        "required": [
                                          "pattern"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "template"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "template": {
                                            "type": "string",
                                            "description": "Template string supporting {value} and {fieldName} placeholders"
                                          }
                                        },
                                        "required": [
                                          "template"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "split"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "delimiter": {
                                            "type": "string",
                                            "description": "String delimiter to split on"
                                          },
                                          "index": {
                                            "type": "integer",
                                            "minimum": 0,
                                            "description": "Index of the split segment to extract"
                                          }
                                        },
                                        "required": [
                                          "delimiter",
                                          "index"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "concat"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "prefix": {
                                            "type": "string",
                                            "default": "",
                                            "description": "Prefix string to prepend"
                                          },
                                          "suffix": {
                                            "type": "string",
                                            "default": "",
                                            "description": "Suffix string to append"
                                          }
                                        },
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "default"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "value": {
                                            "type": "string",
                                            "description": "Default value to use if field is empty"
                                          }
                                        },
                                        "required": [
                                          "value"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "date_format"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "inputFormat": {
                                            "type": "string",
                                            "description": "Input date format (optional, auto-detected if not provided)"
                                          },
                                          "outputFormat": {
                                            "type": "string",
                                            "description": "Output date format string"
                                          }
                                        },
                                        "required": [
                                          "outputFormat"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  }
                                ]
                              },
                              "description": "Optional chain of transforms to apply to the extracted value"
                            }
                          },
                          "required": [
                            "source"
                          ],
                          "additionalProperties": false
                        },
                        "year": {
                          "type": "object",
                          "properties": {
                            "source": {
                              "type": "string",
                              "description": "JSONPath expression to extract value from API response"
                            },
                            "transforms": {
                              "type": "array",
                              "items": {
                                "anyOf": [
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "uppercase"
                                      }
                                    },
                                    "required": [
                                      "type"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "lowercase"
                                      }
                                    },
                                    "required": [
                                      "type"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "trim"
                                      }
                                    },
                                    "required": [
                                      "type"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "regex"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "pattern": {
                                            "type": "string",
                                            "description": "Regular expression pattern to match"
                                          },
                                          "replacement": {
                                            "type": "string",
                                            "default": "",
                                            "description": "Replacement string for regex matches"
                                          }
                                        },
                                        "required": [
                                          "pattern"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "template"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "template": {
                                            "type": "string",
                                            "description": "Template string supporting {value} and {fieldName} placeholders"
                                          }
                                        },
                                        "required": [
                                          "template"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "split"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "delimiter": {
                                            "type": "string",
                                            "description": "String delimiter to split on"
                                          },
                                          "index": {
                                            "type": "integer",
                                            "minimum": 0,
                                            "description": "Index of the split segment to extract"
                                          }
                                        },
                                        "required": [
                                          "delimiter",
                                          "index"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "concat"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "prefix": {
                                            "type": "string",
                                            "default": "",
                                            "description": "Prefix string to prepend"
                                          },
                                          "suffix": {
                                            "type": "string",
                                            "default": "",
                                            "description": "Suffix string to append"
                                          }
                                        },
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "default"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "value": {
                                            "type": "string",
                                            "description": "Default value to use if field is empty"
                                          }
                                        },
                                        "required": [
                                          "value"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "date_format"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "inputFormat": {
                                            "type": "string",
                                            "description": "Input date format (optional, auto-detected if not provided)"
                                          },
                                          "outputFormat": {
                                            "type": "string",
                                            "description": "Output date format string"
                                          }
                                        },
                                        "required": [
                                          "outputFormat"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  }
                                ]
                              },
                              "description": "Optional chain of transforms to apply to the extracted value"
                            }
                          },
                          "required": [
                            "source"
                          ],
                          "additionalProperties": false
                        },
                        "make": {
                          "type": "object",
                          "properties": {
                            "source": {
                              "type": "string",
                              "description": "JSONPath expression to extract value from API response"
                            },
                            "transforms": {
                              "type": "array",
                              "items": {
                                "anyOf": [
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "uppercase"
                                      }
                                    },
                                    "required": [
                                      "type"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "lowercase"
                                      }
                                    },
                                    "required": [
                                      "type"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "trim"
                                      }
                                    },
                                    "required": [
                                      "type"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "regex"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "pattern": {
                                            "type": "string",
                                            "description": "Regular expression pattern to match"
                                          },
                                          "replacement": {
                                            "type": "string",
                                            "default": "",
                                            "description": "Replacement string for regex matches"
                                          }
                                        },
                                        "required": [
                                          "pattern"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "template"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "template": {
                                            "type": "string",
                                            "description": "Template string supporting {value} and {fieldName} placeholders"
                                          }
                                        },
                                        "required": [
                                          "template"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "split"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "delimiter": {
                                            "type": "string",
                                            "description": "String delimiter to split on"
                                          },
                                          "index": {
                                            "type": "integer",
                                            "minimum": 0,
                                            "description": "Index of the split segment to extract"
                                          }
                                        },
                                        "required": [
                                          "delimiter",
                                          "index"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "concat"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "prefix": {
                                            "type": "string",
                                            "default": "",
                                            "description": "Prefix string to prepend"
                                          },
                                          "suffix": {
                                            "type": "string",
                                            "default": "",
                                            "description": "Suffix string to append"
                                          }
                                        },
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "default"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "value": {
                                            "type": "string",
                                            "description": "Default value to use if field is empty"
                                          }
                                        },
                                        "required": [
                                          "value"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "date_format"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "inputFormat": {
                                            "type": "string",
                                            "description": "Input date format (optional, auto-detected if not provided)"
                                          },
                                          "outputFormat": {
                                            "type": "string",
                                            "description": "Output date format string"
                                          }
                                        },
                                        "required": [
                                          "outputFormat"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  }
                                ]
                              },
                              "description": "Optional chain of transforms to apply to the extracted value"
                            }
                          },
                          "required": [
                            "source"
                          ],
                          "additionalProperties": false
                        },
                        "model": {
                          "type": "object",
                          "properties": {
                            "source": {
                              "type": "string",
                              "description": "JSONPath expression to extract value from API response"
                            },
                            "transforms": {
                              "type": "array",
                              "items": {
                                "anyOf": [
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "uppercase"
                                      }
                                    },
                                    "required": [
                                      "type"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "lowercase"
                                      }
                                    },
                                    "required": [
                                      "type"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "trim"
                                      }
                                    },
                                    "required": [
                                      "type"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "regex"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "pattern": {
                                            "type": "string",
                                            "description": "Regular expression pattern to match"
                                          },
                                          "replacement": {
                                            "type": "string",
                                            "default": "",
                                            "description": "Replacement string for regex matches"
                                          }
                                        },
                                        "required": [
                                          "pattern"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "template"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "template": {
                                            "type": "string",
                                            "description": "Template string supporting {value} and {fieldName} placeholders"
                                          }
                                        },
                                        "required": [
                                          "template"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "split"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "delimiter": {
                                            "type": "string",
                                            "description": "String delimiter to split on"
                                          },
                                          "index": {
                                            "type": "integer",
                                            "minimum": 0,
                                            "description": "Index of the split segment to extract"
                                          }
                                        },
                                        "required": [
                                          "delimiter",
                                          "index"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "concat"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "prefix": {
                                            "type": "string",
                                            "default": "",
                                            "description": "Prefix string to prepend"
                                          },
                                          "suffix": {
                                            "type": "string",
                                            "default": "",
                                            "description": "Suffix string to append"
                                          }
                                        },
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "default"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "value": {
                                            "type": "string",
                                            "description": "Default value to use if field is empty"
                                          }
                                        },
                                        "required": [
                                          "value"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "date_format"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "inputFormat": {
                                            "type": "string",
                                            "description": "Input date format (optional, auto-detected if not provided)"
                                          },
                                          "outputFormat": {
                                            "type": "string",
                                            "description": "Output date format string"
                                          }
                                        },
                                        "required": [
                                          "outputFormat"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  }
                                ]
                              },
                              "description": "Optional chain of transforms to apply to the extracted value"
                            }
                          },
                          "required": [
                            "source"
                          ],
                          "additionalProperties": false
                        },
                        "color": {
                          "type": "object",
                          "properties": {
                            "source": {
                              "type": "string",
                              "description": "JSONPath expression to extract value from API response"
                            },
                            "transforms": {
                              "type": "array",
                              "items": {
                                "anyOf": [
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "uppercase"
                                      }
                                    },
                                    "required": [
                                      "type"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "lowercase"
                                      }
                                    },
                                    "required": [
                                      "type"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "trim"
                                      }
                                    },
                                    "required": [
                                      "type"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "regex"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "pattern": {
                                            "type": "string",
                                            "description": "Regular expression pattern to match"
                                          },
                                          "replacement": {
                                            "type": "string",
                                            "default": "",
                                            "description": "Replacement string for regex matches"
                                          }
                                        },
                                        "required": [
                                          "pattern"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "template"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "template": {
                                            "type": "string",
                                            "description": "Template string supporting {value} and {fieldName} placeholders"
                                          }
                                        },
                                        "required": [
                                          "template"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "split"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "delimiter": {
                                            "type": "string",
                                            "description": "String delimiter to split on"
                                          },
                                          "index": {
                                            "type": "integer",
                                            "minimum": 0,
                                            "description": "Index of the split segment to extract"
                                          }
                                        },
                                        "required": [
                                          "delimiter",
                                          "index"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "concat"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "prefix": {
                                            "type": "string",
                                            "default": "",
                                            "description": "Prefix string to prepend"
                                          },
                                          "suffix": {
                                            "type": "string",
                                            "default": "",
                                            "description": "Suffix string to append"
                                          }
                                        },
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "default"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "value": {
                                            "type": "string",
                                            "description": "Default value to use if field is empty"
                                          }
                                        },
                                        "required": [
                                          "value"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "date_format"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "inputFormat": {
                                            "type": "string",
                                            "description": "Input date format (optional, auto-detected if not provided)"
                                          },
                                          "outputFormat": {
                                            "type": "string",
                                            "description": "Output date format string"
                                          }
                                        },
                                        "required": [
                                          "outputFormat"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  }
                                ]
                              },
                              "description": "Optional chain of transforms to apply to the extracted value"
                            }
                          },
                          "required": [
                            "source"
                          ],
                          "additionalProperties": false
                        },
                        "stockNumber": {
                          "type": "object",
                          "properties": {
                            "source": {
                              "type": "string",
                              "description": "JSONPath expression to extract value from API response"
                            },
                            "transforms": {
                              "type": "array",
                              "items": {
                                "anyOf": [
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "uppercase"
                                      }
                                    },
                                    "required": [
                                      "type"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "lowercase"
                                      }
                                    },
                                    "required": [
                                      "type"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "trim"
                                      }
                                    },
                                    "required": [
                                      "type"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "regex"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "pattern": {
                                            "type": "string",
                                            "description": "Regular expression pattern to match"
                                          },
                                          "replacement": {
                                            "type": "string",
                                            "default": "",
                                            "description": "Replacement string for regex matches"
                                          }
                                        },
                                        "required": [
                                          "pattern"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "template"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "template": {
                                            "type": "string",
                                            "description": "Template string supporting {value} and {fieldName} placeholders"
                                          }
                                        },
                                        "required": [
                                          "template"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "split"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "delimiter": {
                                            "type": "string",
                                            "description": "String delimiter to split on"
                                          },
                                          "index": {
                                            "type": "integer",
                                            "minimum": 0,
                                            "description": "Index of the split segment to extract"
                                          }
                                        },
                                        "required": [
                                          "delimiter",
                                          "index"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "concat"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "prefix": {
                                            "type": "string",
                                            "default": "",
                                            "description": "Prefix string to prepend"
                                          },
                                          "suffix": {
                                            "type": "string",
                                            "default": "",
                                            "description": "Suffix string to append"
                                          }
                                        },
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "default"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "value": {
                                            "type": "string",
                                            "description": "Default value to use if field is empty"
                                          }
                                        },
                                        "required": [
                                          "value"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "date_format"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "inputFormat": {
                                            "type": "string",
                                            "description": "Input date format (optional, auto-detected if not provided)"
                                          },
                                          "outputFormat": {
                                            "type": "string",
                                            "description": "Output date format string"
                                          }
                                        },
                                        "required": [
                                          "outputFormat"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  }
                                ]
                              },
                              "description": "Optional chain of transforms to apply to the extracted value"
                            }
                          },
                          "required": [
                            "source"
                          ],
                          "additionalProperties": false
                        },
                        "licensePlate": {
                          "type": "object",
                          "properties": {
                            "source": {
                              "type": "string",
                              "description": "JSONPath expression to extract value from API response"
                            },
                            "transforms": {
                              "type": "array",
                              "items": {
                                "anyOf": [
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "uppercase"
                                      }
                                    },
                                    "required": [
                                      "type"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "lowercase"
                                      }
                                    },
                                    "required": [
                                      "type"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "trim"
                                      }
                                    },
                                    "required": [
                                      "type"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "regex"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "pattern": {
                                            "type": "string",
                                            "description": "Regular expression pattern to match"
                                          },
                                          "replacement": {
                                            "type": "string",
                                            "default": "",
                                            "description": "Replacement string for regex matches"
                                          }
                                        },
                                        "required": [
                                          "pattern"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "template"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "template": {
                                            "type": "string",
                                            "description": "Template string supporting {value} and {fieldName} placeholders"
                                          }
                                        },
                                        "required": [
                                          "template"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "split"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "delimiter": {
                                            "type": "string",
                                            "description": "String delimiter to split on"
                                          },
                                          "index": {
                                            "type": "integer",
                                            "minimum": 0,
                                            "description": "Index of the split segment to extract"
                                          }
                                        },
                                        "required": [
                                          "delimiter",
                                          "index"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "concat"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "prefix": {
                                            "type": "string",
                                            "default": "",
                                            "description": "Prefix string to prepend"
                                          },
                                          "suffix": {
                                            "type": "string",
                                            "default": "",
                                            "description": "Suffix string to append"
                                          }
                                        },
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "default"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "value": {
                                            "type": "string",
                                            "description": "Default value to use if field is empty"
                                          }
                                        },
                                        "required": [
                                          "value"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  },
                                  {
                                    "type": "object",
                                    "properties": {
                                      "type": {
                                        "type": "string",
                                        "const": "date_format"
                                      },
                                      "config": {
                                        "type": "object",
                                        "properties": {
                                          "inputFormat": {
                                            "type": "string",
                                            "description": "Input date format (optional, auto-detected if not provided)"
                                          },
                                          "outputFormat": {
                                            "type": "string",
                                            "description": "Output date format string"
                                          }
                                        },
                                        "required": [
                                          "outputFormat"
                                        ],
                                        "additionalProperties": false
                                      }
                                    },
                                    "required": [
                                      "type",
                                      "config"
                                    ],
                                    "additionalProperties": false
                                  }
                                ]
                              },
                              "description": "Optional chain of transforms to apply to the extracted value"
                            }
                          },
                          "required": [
                            "source"
                          ],
                          "additionalProperties": false
                        }
                      },
                      "additionalProperties": false,
                      "description": "Field mappings applied to the enrichment response.\n\nMapped values are **merged** with the primary response result. Non-null enrichment values will overwrite existing fields from the primary response.\n\nUses the same JSONPath syntax as primary fetch trigger mappings."
                    }
                  },
                  "required": [
                    "endpoint",
                    "mappings"
                  ],
                  "additionalProperties": false
                },
                "default": [],
                "description": "Optional enrichments for chained API calls.\n\nEach enrichment makes a **secondary API call** using data from the primary response, then merges the results into the final `FetchResult`.\n\n**Use Cases:**\n- Fetch customer address from a separate endpoint\n- Get additional details that require the primary record's ID\n- Call different APIs conditionally based on primary response data\n\n**Execution Flow:**\n1. Primary fetch request executes and maps response\n2. Each enrichment's `condition` is evaluated against raw primary response\n3. Enrichments with passing conditions execute in order\n4. Results merge into final response (non-null values overwrite)\n\n**Error Handling:**\nEnrichment failures are non-fatal. If an enrichment fails, the primary data is preserved and processing continues with remaining enrichments."
              }
            },
            "required": [
              "enabled",
              "authProvider",
              "httpMethod",
              "endpoint",
              "mappings"
            ],
            "additionalProperties": false
          },
          "default": {},
          "description": "Fetch trigger configurations. Keys are trigger type identifiers (e.g., 'contract-detail', 'consumer-detail', 'vehicle-detail'). The trigger type is arbitrary - form code defines what trigger types it fires, and the config defines how to handle each one."
        }
      },
      "required": [
        "enabled"
      ],
      "additionalProperties": false,
      "description": "Configure triggered connectors for move form autocomplete and data fetching from partner APIs",
      "x-hopdrive-label": "Connector Triggers",
      "x-hopdrive-writable-at-levels": [
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin",
        "dealer-admin",
        "dealer-super-admin"
      ],
      "x-hopdrive-category": "integrations"
    },
    "minimum_certification_ids": {
      "type": "array",
      "items": {
        "type": "string"
      },
      "description": "Minimum certification IDs all drivers must have in order to be receive move offers.",
      "x-hopdrive-label": "Minimum Certification IDs",
      "x-hopdrive-writable-at-levels": [
        "region",
        "organization",
        "customer",
        "workflowset",
        "raterulegroup",
        "move"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "custom-settings"
    },
    "billing_frequency": {
      "type": "string",
      "enum": [
        "monthly",
        "weekly",
        "daily"
      ],
      "description": "Determines how often we generate invoices for this client, which directly affects how often they are billed. This should be set to monthly, unless the client specifically requests otherwise.",
      "x-hopdrive-label": "Billing Frequency",
      "x-hopdrive-writable-at-levels": [
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "billing"
    },
    "payment_terms": {
      "type": "integer",
      "minimum": 1,
      "maximum": 30,
      "description": "Payment terms determine how many days we give the client to pay an invoice after it's end date. This should be set to 30 days, unless the client specifically requests otherwise.",
      "x-hopdrive-label": "Payment Terms",
      "x-hopdrive-writable-at-levels": [
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "billing"
    },
    "auto_pay": {
      "type": "boolean",
      "description": "Activate/Deactivate auto-pay for this client. Auto-pay determines whether or not we automatically charge the client's credit card after an invoice closes.",
      "x-hopdrive-label": "Auto Pay",
      "x-hopdrive-writable-at-levels": [
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "billing"
    },
    "isShuttle": {
      "type": "boolean",
      "description": "System flag marking this move as part of a shuttle sequence. Set by shuttle planning, not user-configurable.",
      "x-hopdrive-label": "Is Shuttle",
      "x-hopdrive-writable-at-levels": [
        "move"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "operational-state"
    },
    "firstShuttleMove": {
      "type": "boolean",
      "description": "System flag marking this as the first move in a shuttle sequence. Set by shuttle planning, not user-configurable.",
      "x-hopdrive-label": "First Shuttle Move",
      "x-hopdrive-writable-at-levels": [
        "move"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "operational-state"
    },
    "lastShuttleMove": {
      "type": "boolean",
      "description": "System flag marking this as the last move in a shuttle sequence. Triggers chase vehicle status to \"returned\". Set by shuttle planning, not user-configurable.",
      "x-hopdrive-label": "Last Shuttle Move",
      "x-hopdrive-writable-at-levels": [
        "move"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "operational-state"
    },
    "chase_id": {
      "type": "string",
      "format": "uuid",
      "description": "UUID linking chase moves together for ridegroup management, cost allocation, and status sync. Set by shuttle/chase planning, not user-configurable.",
      "x-hopdrive-label": "Chase ID",
      "x-hopdrive-writable-at-levels": [
        "move"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "operational-state"
    },
    "parent_chase_id": {
      "type": "string",
      "format": "uuid",
      "description": "UUID referencing the parent move in a chase sequence. Set by autoplanner, not user-configurable.",
      "x-hopdrive-label": "Parent Chase ID",
      "x-hopdrive-writable-at-levels": [
        "move"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "operational-state"
    },
    "batch": {
      "type": "object",
      "properties": {
        "order_ref": {
          "type": "string",
          "description": "External order reference from Super Dispatch"
        },
        "size": {
          "type": "integer",
          "exclusiveMinimum": 0,
          "description": "Number of vehicles in this batch"
        }
      },
      "additionalProperties": false,
      "description": "Groups moves from multi-vehicle Super Dispatch orders. Contains order reference and batch size. Set by integrations, not user-configurable.",
      "x-hopdrive-label": "Batch",
      "x-hopdrive-writable-at-levels": [
        "move"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "operational-state"
    },
    "ride_orchestration": {
      "type": "object",
      "properties": {
        "last_estimate_at": {
          "type": "string",
          "description": "ISO timestamp of last estimate fetch"
        },
        "next_estimate_at": {
          "type": "string",
          "description": "ISO timestamp for next scheduled estimate refresh"
        },
        "rideshare_eta_seconds": {
          "type": "number",
          "description": "Estimated time of arrival in seconds"
        },
        "selected_provider": {
          "type": "string",
          "enum": [
            "uber",
            "lyft"
          ],
          "description": "Selected rideshare provider (uber or lyft)"
        },
        "selected_product": {
          "type": "string",
          "description": "Selected product/tier (e.g., UberX, Lyft XL)"
        },
        "target_pickup_time": {
          "type": "string",
          "description": "Target pickup time for ride-after scenarios (ISO timestamp)"
        },
        "best_time_to_call": {
          "type": "string",
          "description": "Optimal time to request the ride (ISO timestamp)"
        },
        "booking_lead_time_seconds": {
          "type": "number",
          "description": "Lead time needed before target pickup"
        },
        "calculation_method": {
          "type": "string",
          "description": "Method used to calculate timing (e.g., \"eta-based\", \"fixed\")"
        }
      },
      "additionalProperties": false,
      "description": "Rideshare booking state including estimates, provider selection, and timing calculations. Set by hoprides service, not user-configurable.",
      "x-hopdrive-label": "Ride Orchestration",
      "x-hopdrive-writable-at-levels": [
        "move"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "operational-state"
    },
    "superDispatch": {
      "type": "object",
      "properties": {
        "vehicle_id": {
          "type": "number",
          "description": "Super Dispatch vehicle ID"
        },
        "reference_num": {
          "type": "number",
          "description": "Super Dispatch reference number for status sync"
        }
      },
      "additionalProperties": false,
      "description": "Super Dispatch integration reference data for status sync. Set by super-dispatch-integration, not user-configurable.",
      "x-hopdrive-label": "Super Dispatch",
      "x-hopdrive-writable-at-levels": [
        "move"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "operational-state"
    },
    "referenceNumbers": {
      "type": "object",
      "additionalProperties": {
        "type": "string"
      },
      "description": "External system reference IDs from CarsArrive, Central Dispatch, etc. Set by integrations, not user-configurable.",
      "x-hopdrive-label": "Reference Numbers",
      "x-hopdrive-writable-at-levels": [
        "move"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "operational-state"
    },
    "isGhost": {
      "type": "boolean",
      "description": "Marks a placeholder move requiring full VIN capture. Set when created from Super Dispatch with shell vehicle data. Not user-configurable.",
      "x-hopdrive-label": "Is Ghost",
      "x-hopdrive-writable-at-levels": [
        "move"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "operational-state"
    },
    "vin": {
      "type": "object",
      "properties": {
        "capture": {
          "type": "string",
          "enum": [
            "full",
            "partial",
            "none"
          ],
          "description": "Required VIN capture level in driver workflow"
        }
      },
      "additionalProperties": false,
      "description": "VIN capture requirements for driver workflow. Set by system for ghost moves, not user-configurable.",
      "x-hopdrive-label": "VIN Capture",
      "x-hopdrive-writable-at-levels": [
        "move"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "operational-state"
    },
    "triangle_move": {
      "type": "boolean",
      "description": "Marks move as triangle (ride before AND after charged to customer). Set via admin UI when customer has allow_triangle_moves permission.",
      "x-hopdrive-label": "Triangle Move",
      "x-hopdrive-writable-at-levels": [
        "move"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "operational-state"
    },
    "isFlex": {
      "type": "boolean",
      "description": "Marks a round trip as flex type for scenario detection. Set by system, not user-configurable.",
      "x-hopdrive-label": "Is Flex",
      "x-hopdrive-writable-at-levels": [
        "move"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "operational-state"
    },
    "moveInput": {
      "type": "object",
      "additionalProperties": {},
      "description": "Output data from the dynamic form presented during move create in React. Contains indeterminate key-value pairs (one level deep). Not user-configurable.",
      "x-hopdrive-label": "Move Input",
      "x-hopdrive-writable-at-levels": [
        "move"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "operational-state"
    }
  },
  "additionalProperties": false
}
